import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { CaseSLAPanel } from '../components/CaseSLAPanel';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Briefcase,
  Plus,
  Search,
  Filter,
  AlertTriangle,
  Clock,
  CheckCircle,
  Archive,
  Users,
  FileText,
  Eye,
  MessageSquare,
  Link2,
  Phone,
  Mail,
  Calendar,
  Paperclip,
  FileIcon,
  Image,
  Download,
  ExternalLink,
  History,
  StickyNote,
  ShieldAlert,
  Shield,
  Building2,
  Headphones,
  Tag,
  Maximize2,
  Send,
  ArrowUpRight,
  LayoutGrid,
  Layers,
  ListPlus,
  Tags,
  Copy,
  Inbox,
  Activity,
  BarChart3,
  User,
  FlaskConical,
  XCircle,
  RefreshCw,
  Lock,
  Zap,
  Edit2,
  Settings,
  Loader2,
  GitMerge,
  Trash2
} from 'lucide-react';
import CasosDashboard from '../components/CasosDashboard';
import { useAuth } from '../contexts/AuthContext';
import { ExcelExportButton } from '../components/ExcelExportButton';
import { CasesDialogs } from '../components/cases/CasesDialogs';
import { EliminarCasosDialog } from '../components/cases/EliminarCasosDialog';
import { PapeleraCasosDialog } from '../components/cases/PapeleraCasosDialog';
import { useSeccionPermisos } from '../hooks/useSeccionPermisos';
import { useCustomCaseStatuses } from '../hooks/useCustomCaseStatuses';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { DataTable } from '../components/DataTable';
import CasesFilters from '../components/casos/CasesFilters';
import EmailBulkDialog from '../components/casos/EmailBulkDialog';
import CasesGroupedView from '../components/CasesGroupedView';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const ESTADOS = [
  { value: 'ABIERTO', label: 'Abierto', color: 'bg-blue-100 text-blue-800', icon: Briefcase },
  { value: 'PENDIENTE', label: 'Pendiente', color: 'bg-amber-100 text-amber-800', icon: Clock },
  { value: 'EN_INVESTIGACION', label: 'En Investigación', color: 'bg-yellow-100 text-yellow-800', icon: Search },
  { value: 'PENDIENTE_APROBACION', label: 'Pendiente Aprobación', color: 'bg-purple-100 text-purple-800', icon: Clock },
  { value: 'EN_REVISION', label: 'En Revisión', color: 'bg-indigo-100 text-indigo-800', icon: Eye },
  { value: 'ESCALADO', label: 'Escalado', color: 'bg-rose-100 text-rose-800', icon: AlertTriangle },
  { value: 'ESPERANDO_COMPLIANCE', label: 'Esperando Compliance', color: 'bg-orange-100 text-orange-800', icon: Lock },
  { value: 'BLOQUEO_PREVENTIVO', label: 'Bloqueo Preventivo', color: 'bg-red-200 text-red-900', icon: ShieldAlert },
  { value: 'EN_VIGILANCIA', label: 'En Vigilancia', color: 'bg-cyan-100 text-cyan-800', icon: Eye },
  { value: 'TESTING', label: 'Testing', color: 'bg-cyan-100 text-cyan-800', icon: FlaskConical },
  { value: 'CERRADO', label: 'Cerrado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { value: 'CANCELADO', label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle },
  { value: 'ARCHIVADO', label: 'Archivado', color: 'bg-gray-100 text-gray-800', icon: Archive },
];

const PRIORIDADES = [
  { value: 'CRITICA', label: 'Crítica', color: 'bg-red-100 text-red-800' },
  { value: 'ALTA', label: 'Alta', color: 'bg-orange-100 text-orange-800' },
  { value: 'MEDIA', label: 'Media', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'BAJA', label: 'Baja', color: 'bg-green-100 text-green-800' },
];

const TIPOS_CASO = [
  { value: 'FRAUDE', label: 'Fraude', color: 'bg-red-100 text-red-800', icon: ShieldAlert },
  { value: 'SOPORTE', label: 'Soporte', color: 'bg-blue-100 text-blue-800', icon: Headphones },
  { value: 'ALERTA', label: 'Alerta', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
  { value: 'COMPLIANCE', label: 'Compliance', color: 'bg-purple-100 text-purple-800', icon: FileText },
  { value: 'OTRO', label: 'Otro', color: 'bg-gray-100 text-gray-800', icon: Tag },
];

const TIPOS_NOTA = [
  { value: 'nota', label: 'Nota General', icon: StickyNote },
  { value: 'llamada_cliente', label: 'Llamada con Cliente', icon: Phone },
  { value: 'email', label: 'Comunicación por Email', icon: Mail },
  { value: 'reunion', label: 'Reunión', icon: Calendar },
];

const TIPOS_DOCUMENTO = [
  { value: 'documento', label: 'Documento', icon: FileText },
  { value: 'imagen', label: 'Imagen', icon: Image },
  { value: 'otro', label: 'Otro', icon: FileIcon },
];

const TIMELINE_ICONS = {
  CREACION: Briefcase,
  COMENTARIO: MessageSquare,
  CAMBIO_ESTADO: CheckCircle,
  CAMBIO_PRIORIDAD: AlertTriangle,
  DOCUMENTO: Paperclip,
  NOTA: StickyNote,
  ASIGNACION: Users,
  ALERTA_AGREGADA: AlertTriangle,
};

const TIMELINE_COLORS = {
  CREACION: 'bg-blue-500',
  COMENTARIO: 'bg-green-500',
  CAMBIO_ESTADO: 'bg-purple-500',
  CAMBIO_PRIORIDAD: 'bg-orange-500',
  DOCUMENTO: 'bg-cyan-500',
  NOTA: 'bg-yellow-500',
  ASIGNACION: 'bg-indigo-500',
  ALERTA_AGREGADA: 'bg-red-500',
};

const CasesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['admin', 'superadmin', 'ADMINISTRADOR', 'ADMIN'].includes(user?.rol);
  const customEstados = useCustomCaseStatuses();
  const ALL_ESTADOS = [...ESTADOS, ...customEstados];
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [filters, setFilters] = useState(() => {
    const defaults = { estado: '', prioridad: '', asignacion: '', cuenta_bloqueada: '', es_externo: '', tiene_vinculos: '', ros_en_proceso: '', waya_verificacion: '', fecha_desde: '', fecha_hasta: '', cvus: '', cuits: '', solicitud_bloqueo: '' };
    try {
      const stored = JSON.parse(localStorage.getItem('caseNavFilters') || '{}');
      const restored = {};
      Object.keys(defaults).forEach((k) => { if (stored[k]) restored[k] = stored[k]; });
      if (stored.min_alertas) restored.min_alertas = stored.min_alertas;
      return { ...defaults, ...restored };
    } catch { return defaults; }
  });
  const [searchTerm, setSearchTerm] = useState(() => {
    try { return JSON.parse(localStorage.getItem('caseNavFilters') || '{}').search || ''; } catch { return ''; }
  });
  const [searchDebounce, setSearchDebounce] = useState(() => {
    try { return JSON.parse(localStorage.getItem('caseNavFilters') || '{}').search || ''; } catch { return ''; }
  });
  const [showDashboard, setShowDashboard] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [generandoInforme, setGenerandoInforme] = useState(false);
  const [showGenerarListaDialog, setShowGenerarListaDialog] = useState(false);
  const [listaTipo, setListaTipo] = useState('cvu');
  const [listaValores, setListaValores] = useState('');
  const [listaAgrupacion, setListaAgrupacion] = useState('individual');
  const [listaEstado, setListaEstado] = useState('ABIERTO');
  const [listaPrioridad, setListaPrioridad] = useState('MEDIA');
  const [listaTitulo, setListaTitulo] = useState('');
  const [generandoLista, setGenerandoLista] = useState(false);
  const [listaProgress, setListaProgress] = useState(null);
  const [generarJobId, setGenerarJobId] = useState(null);
  const [revalidandoBulk, setRevalidandoBulk] = useState(false);
  const [revalidarProgress, setRevalidarProgress] = useState(null);
  const [emailBulkResults, setEmailBulkResults] = useState(null);
  const [showEmailBulkResults, setShowEmailBulkResults] = useState(false);
  const [showEmailBulkCompose, setShowEmailBulkCompose] = useState(false);
  const [cuitCoverage, setCuitCoverage] = useState(null);
  const [emailBulkSentItems, setEmailBulkSentItems] = useState([]);
  const [reprocesando, setReprocesando] = useState(false);
  const [rotacionReproceso, setRotacionReproceso] = useState(null);
  const [cuentaReproceso, setCuentaReproceso] = useState('');
  const [showCierreSinRosDialog, setShowCierreSinRosDialog] = useState(false);
  const [verificandoBulk, setVerificandoBulk] = useState(false);
  const [verificandoTodos, setVerificandoTodos] = useState(false);
  const [limpiandoVerif, setLimpiandoVerif] = useState(false);
  const [verificarProgress, setVerificarProgress] = useState(null);
  const [verificarResults, setVerificarResults] = useState(null);
  const [verificarFiltro, setVerificarFiltro] = useState('todos');
  const [showVerificarResults, setShowVerificarResults] = useState(false);
  const [cierreBloquear, setCierreBloquear] = useState(false);
  const [cierreCerrarCasos, setCierreCerrarCasos] = useState(true);
  const [cerrandoSinRos, setCerrandoSinRos] = useState(false);
  const [cierreProgress, setCierreProgress] = useState(null);
  const [showInformeDialog, setShowInformeDialog] = useState(false);
  const [informeTitulo, setInformeTitulo] = useState('');
  const [informeResumen, setInformeResumen] = useState('');
  const [informeSecciones, setInformeSecciones] = useState({
    incluir_kpis: true,
    incluir_graficos: true,
    incluir_tabla_resumen: true,
    incluir_detalle: true,
    incluir_alertas: true,
    incluir_montos: true,
  });
  const [informeColumnas, setInformeColumnas] = useState({
    cliente: true,
    cvu: true,
    estado: true,
    alertas: true,
    monto_periodo: true,
    monto_historico: true,
  });
  const [kpiCardsConfig, setKpiCardsConfig] = useState({ available_cards: [], active_cards: [] });
  const [showKpiConfigDialog, setShowKpiConfigDialog] = useState(false);
  const [selectedForLink, setSelectedForLink] = useState({});
  const [showVinculacion, setShowVinculacion] = useState(false);
  const [vinculacionResult, setVinculacionResult] = useState(null);
  const [vinculacionLoading, setVinculacionLoading] = useState(false);
  
  // Paginación
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });
  
  const [newCase, setNewCase] = useState({
    titulo: '',
    descripcion: '',
    prioridad: 'MEDIA',
    tipo: 'ALERTA',
    alertas_ids: [],
    tags: []
  });
  
  const [newComment, setNewComment] = useState('');
  const [newNote, setNewNote] = useState({ texto: '', tipo: 'nota' });
  const [newDocument, setNewDocument] = useState({ nombre: '', url: '', tipo: 'documento' });
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showDocForm, setShowDocForm] = useState(false);
  const [availableAlerts, setAvailableAlerts] = useState([]);
  
  // Approval workflow states
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showDeriveDialog, setShowDeriveDialog] = useState(false);
  const [groups, setGroups] = useState([]);
  const [approvalData, setApprovalData] = useState({
    accion: 'CERRAR',
    descripcion: '',
    grupo_aprobador_id: '',
    prioridad: 'MEDIA'
  });
  const [deriveData, setDeriveData] = useState({
    grupo_destino_id: '',
    motivo: '',
    prioridad: ''
  });

  // Selección múltiple para asignación masiva
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  const permCasos = useSeccionPermisos('gestion-de-casos', 'cases');
  const puedeEliminar = !!permCasos?.eliminar;
  const [showEliminarDialog, setShowEliminarDialog] = useState(false);
  const [showPapelera, setShowPapelera] = useState(false);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [bulkAssignData, setBulkAssignData] = useState({ responsable_id: '', grupo_id: '' });
  const [showBulkCloseDialog, setShowBulkCloseDialog] = useState(false);
  const [bulkCloseData, setBulkCloseData] = useState({ tipo_resolucion: '', observaciones: '' });
  const [showBulkEstadoDialog, setShowBulkEstadoDialog] = useState(false);
  const [showClasifDialog, setShowClasifDialog] = useState(false);
  const [clasificando, setClasificando] = useState(false);
  const [bulkEstadoData, setBulkEstadoData] = useState({ estado: '', observaciones: '' });
  const [changingEstado, setChangingEstado] = useState(false);
  const [bulkEstadoProgress, setBulkEstadoProgress] = useState(null);

  // ===== VIGILAR (crear sucursal + En Vigilancia desde casos) =====
  const [showVigilarDialog, setShowVigilarDialog] = useState(false);
  const [viewMode, setViewMode] = useState('tabla'); // 'tabla' | 'agrupada'
  const [vigilarEmpresas, setVigilarEmpresas] = useState([]);
  const [vigilarRubros, setVigilarRubros] = useState([]);
  const [vigilarEmpresaId, setVigilarEmpresaId] = useState('');
  const [vigilarRubroId, setVigilarRubroId] = useState('');
  const [vigilarLoadingCfg, setVigilarLoadingCfg] = useState(false);
  const [vigilarStarting, setVigilarStarting] = useState(false);
  const [vigilarJob, setVigilarJob] = useState(null);
  // ===== Generar ROS desde casos seleccionados =====
  const [showGenRosDialog, setShowGenRosDialog] = useState(false);
  const [genRosTemplates, setGenRosTemplates] = useState([]);
  const [genRosTipos, setGenRosTipos] = useState([]);
  const [genRosTemplate, setGenRosTemplate] = useState('none');
  const [genRosTipo, setGenRosTipo] = useState('lavado_activos');
  const [genRosSubmitting, setGenRosSubmitting] = useState(false);
  const [genRosProgress, setGenRosProgress] = useState(null);
  const vigilarPollRef = useRef(null);
  const [closingBulk, setClosingBulk] = useState(false);
  const [tiposResolucion, setTiposResolucion] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('caseNavFilters') || '{}');
      if (s.sort_by) return { key: s.sort_by, direction: s.sort_order || 'desc' };
    } catch { /* ignore */ }
    return { key: 'fecha', direction: 'desc' };
  });

  const loadCases = useCallback(async (page = pagination.page) => {
    // Modo "filtrar por CUITs": 1 caso por CUIT + reporte de cobertura
    if (filters.cuits && filters.cuits.trim()) {
      try {
        setLoading(true);
        const res = await api.post('/cases/buscar-por-cuits', { cuits: filters.cuits }, { timeout: 60000 });
        setCases(res.data.items || []);
        setCuitCoverage({
          encontrados: res.data.encontrados || [],
          no_encontrados: res.data.no_encontrados || [],
          total_cuits: res.data.total_cuits || 0,
        });
        setPagination(prev => ({ ...prev, page: 1, total: (res.data.items || []).length, pages: 1 }));
      } catch (error) {
        toast.error('No se pudo filtrar por CUITs. Reintentá.');
      } finally {
        setLoading(false);
      }
      api.get('/cases/statistics', { timeout: 30000 }).then(r => setStats(r.data)).catch(() => {});
      api.get('/cases/kpis-dashboard', { timeout: 30000 }).then(r => setKpis(r.data)).catch(() => {});
      return;
    }
    setCuitCoverage(null);
    try {
      setLoading(true);
      const params = {
        skip: (page - 1) * pagination.limit,
        limit: pagination.limit,
        include_total: true
      };
      if (filters.estado) params.estado = filters.estado;
      if (filters.prioridad) params.prioridad = filters.prioridad;
      if (filters.asignacion) params.asignacion = filters.asignacion;
      if (filters.cuenta_bloqueada) params.cuenta_bloqueada = filters.cuenta_bloqueada;
      if (filters.es_externo) params.es_externo = filters.es_externo;
      if (filters.min_alertas) {
        if (filters.min_alertas.startsWith('exact')) {
          const n = parseInt(filters.min_alertas.replace('exact', ''));
          params.min_alertas = n;
          params.max_alertas = n;
        } else {
          params.min_alertas = filters.min_alertas;
        }
      }
      if (filters.tiene_vinculos) params.tiene_vinculos = filters.tiene_vinculos;
      if (filters.ros_en_proceso) params.ros_en_proceso = filters.ros_en_proceso;
      if (filters.waya_verificacion) params.waya_verificacion = filters.waya_verificacion;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      if (filters.cvus && filters.cvus.trim()) params.cvus = filters.cvus;
      if (filters.cuits && filters.cuits.trim()) params.cuits = filters.cuits;
      if (filters.solicitud_bloqueo) params.solicitud_bloqueo = filters.solicitud_bloqueo;
      if (searchDebounce) params.search = searchDebounce;
      if (sortConfig.key) {
        params.sort_by = sortConfig.key;
        params.sort_order = sortConfig.direction;
      }
      
      // La LISTA es la consulta crítica: se hace sola para que un fallo/lentitud
      // de statistics o kpis NUNCA deje la tabla en blanco.
      // Timeout corto (45s) para que una request colgada falle rápido y se pueda
      // reintentar, en vez de dejar la UI muerta hasta el timeout global de 5min.
      const casesRes = await api.get('/cases', { params, timeout: 45000 });

      // Respuesta con paginación
      if (casesRes.data.data) {
        setCases(casesRes.data.data);
        setPagination(prev => ({
          ...prev,
          page: casesRes.data.page,
          total: casesRes.data.total,
          pages: casesRes.data.pages
        }));
      } else {
        // Fallback si no hay paginación
        setCases(casesRes.data);
      }
    } catch (error) {
      // No blanquear la lista existente ante un error transitorio
      toast.error('No se pudieron cargar los casos. Reintentá.');
    } finally {
      setLoading(false);
    }

    // Stats y KPIs: independientes y no bloqueantes (no afectan el render de la lista)
    api.get('/cases/statistics', { timeout: 30000 }).then(r => setStats(r.data)).catch(() => {});
    api.get('/cases/kpis-dashboard', { timeout: 30000 }).then(r => setKpis(r.data)).catch(() => {});
  }, [filters, pagination.limit, searchDebounce, sortConfig]);

  // Debounce de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadCases(1);  // Cargar página 1 al cambiar filtros, limit o búsqueda
  }, [filters, pagination.limit, searchDebounce, sortConfig]);

  // Persistir filtros/búsqueda/orden para que se restauren al volver desde un caso
  useEffect(() => {
    try {
      const navFilters = { ...filters, search: searchDebounce, sort_by: sortConfig.key, sort_order: sortConfig.direction };
      localStorage.setItem('caseNavFilters', JSON.stringify(navFilters));
    } catch { /* ignore */ }
  }, [filters, searchDebounce, sortConfig]);
  
  // Cargar configuración de KPI cards
  useEffect(() => {
    api.get('/config/cases-kpi-cards').then(r => setKpiCardsConfig(r.data)).catch(() => {});
    api.get('/cases/tipos-resolucion').then(r => setTiposResolucion(r.data)).catch(() => {});
  }, []);
  
  const saveKpiCardsConfig = async (activeCards) => {
    try {
      await api.put('/config/cases-kpi-cards', { active_cards: activeCards });
      setKpiCardsConfig(prev => ({ ...prev, active_cards: activeCards }));
      toast.success('Tarjetas actualizadas');
      setShowKpiConfigDialog(false);
    } catch {
      toast.error('Error al guardar configuración');
    }
  };
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      loadCases(newPage);
    }
  };
  
  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: parseInt(newLimit), page: 1 }));
  };

  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const getEstadoStyle = (estado) => ALL_ESTADOS.find(e => e.value === estado) || { label: estado, color: 'bg-gray-100 text-gray-800' };
  const getPrioridadStyle = (prioridad) => PRIORIDADES.find(p => p.value === prioridad) || { label: prioridad, color: 'bg-gray-100 text-gray-800' };
  const getTipoStyle = (tipo) => TIPOS_CASO.find(t => t.value === tipo) || { label: tipo, color: 'bg-gray-100 text-gray-800', icon: Tag };

  const caseColumns = [
    {
      key: 'seleccionar', label: '', sortable: false, filterable: false, resizable: false, width: 36, minWidth: 36,
      render: (row) => (
        <input type="checkbox" checked={!!selectedForLink[row.id]}
          onChange={(e) => { e.stopPropagation(); setSelectedForLink(prev => ({ ...prev, [row.id]: e.target.checked })); }}
          className="rounded" onClick={(e) => e.stopPropagation()} />
      )
    },
    {
      key: 'fecha', label: 'Fecha', sortable: true, sortKey: 'fecha',
      filterable: false, resizable: true, width: 90,
      render: (row) => {
        const dateStr = row.created_at || row.fecha_creacion;
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
      }
    },
    {
      key: 'titulo', label: 'Titulo', sortable: true, sortKey: 'titulo',
      filterable: false, resizable: true, width: 220, minWidth: 150,
      render: (row) => (
        <div>
          <div className="font-medium text-slate-900 flex items-center gap-1.5">
            {row.sla_estado === 'VENCIDO' && <Clock size={14} className="text-red-500 flex-shrink-0" />}
            {row.sla_estado === 'PROXIMO_VENCER' && <Clock size={14} className="text-amber-500 flex-shrink-0" />}
            {row.titulo}
          </div>
          {row.descripcion && <div className="text-xs text-slate-500 truncate max-w-[200px]">{row.descripcion}</div>}
        </div>
      )
    },
    {
      key: 'cliente', label: 'Cliente', sortable: true, sortKey: 'cliente',
      filterable: false, resizable: true, width: 180, minWidth: 120,
      render: (row) => {
        const cuitDigits = (row.cuit || '').replace(/\D/g, '');
        const cuitEsReal = cuitDigits.length === 11;
        const cvuVal = row.cvu_principal || (!cuitEsReal && cuitDigits.length >= 20 ? row.cuit : null);
        return row.nombre_padron ? (
          <div>
            <div className="font-medium text-slate-800 text-sm flex items-center gap-1.5">
              {row.nombre_padron}
              {row.es_externo && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">Externo</span>}
              {row.total_casos_cuit > 1 && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200" data-testid={`case-mascasos-${row.id}`} title={`Este CUIT tiene ${row.total_casos_cuit} casos`}>+{row.total_casos_cuit - 1} casos</span>}
            </div>
            {cuitEsReal && <div className="text-xs text-slate-500 font-mono" data-testid={`case-cuit-${row.id}`}>CUIT: {row.cuit}</div>}
            {cvuVal && <div className="text-xs text-slate-400 font-mono" title="CVU">{cvuVal}</div>}
          </div>
        ) : (cuitEsReal || cvuVal) ? (
          <div className="flex flex-col gap-0.5">
            {cuitEsReal
              ? <span className="text-xs text-slate-500 font-mono" data-testid={`case-cuit-${row.id}`}>CUIT: {row.cuit}</span>
              : <span className="text-xs text-slate-400 font-mono" title="CVU">{cvuVal}</span>}
            {row.es_externo && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200 w-fit">Externo</span>}
            {row.total_casos_cuit > 1 && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200 w-fit" data-testid={`case-mascasos-${row.id}`} title={`Este CUIT tiene ${row.total_casos_cuit} casos`}>+{row.total_casos_cuit - 1} casos</span>}
          </div>
        ) : <span className="text-xs text-slate-400">-</span>;
      }
    },
    {
      key: 'tipo', label: 'Tipo', sortable: true, sortKey: 'tipo',
      filterable: true, filterType: 'select',
      filterOptions: TIPOS_CASO.map(t => ({ value: t.value, label: t.label })),
      resizable: true, width: 120,
      render: (row) => {
        const style = getTipoStyle(row.tipo);
        const TipoIcon = style.icon || Tag;
        return <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${style.color}`}><TipoIcon size={12} />{style.label}</span>;
      }
    },
    {
      key: 'estado', label: 'Estado', sortable: true, sortKey: 'estado',
      filterable: false, resizable: true, width: 140,
      render: (row) => {
        const style = getEstadoStyle(row.estado);
        return (
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.color}`}>{style.label}</span>
              {row.estado === 'PENDIENTE_APROBACION' && <Clock size={14} className="text-purple-600 animate-pulse" />}
            </span>
            {row.reactivado_desde_vigilancia && row.estado === 'ABIERTO' && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700 border border-orange-200 inline-flex items-center gap-0.5 w-fit">
                <Zap size={10} />Reactivado
              </span>
            )}
            {(row.last_monitor_update || row.updated_by_monitor) && !row.reactivado_desde_vigilancia && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-200 w-fit">
                Actualizado
              </span>
            )}
            {row.estado === 'EN_VIGILANCIA' && (row.vigilancia_sucursal_id || row.vigilancia_manual) && (
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-cyan-100 text-cyan-700 border border-cyan-200 inline-flex items-center gap-0.5 w-fit"
                title={`${row.vigilancia_sucursal_id ? 'Sucursal Waya #' + row.vigilancia_sucursal_id : 'Configurada'}${row.vigilancia_manual ? ' (manual)' : ''}${row.vigilancia_email ? ' · ' + row.vigilancia_email : ''}${row.vigilancia_fecha ? ' · ' + new Date(row.vigilancia_fecha).toLocaleString('es-AR') : ''}`}
                data-testid={`vigilancia-badge-${row.id}`}
              >
                <Eye size={10} />
                {row.vigilancia_sucursal_id ? `Suc #${row.vigilancia_sucursal_id}` : 'Configurada'}
                {row.vigilancia_manual && <span className="font-normal opacity-80">· manual</span>}
                {row.vigilancia_fecha && <span className="font-normal opacity-80">· {new Date(row.vigilancia_fecha).toLocaleDateString('es-AR')}</span>}
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'bloqueo', label: 'Bloqueo', sortable: false,
      filterable: false, resizable: true, width: 80, defaultVisible: true,
      render: (row) => row.cuenta_bloqueada ? (
        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-red-600 text-white inline-flex items-center gap-0.5"><Lock size={10} />Bloqueada</span>
      ) : <span className="text-slate-300 text-xs">-</span>
    },
    {
      key: 'prioridad', label: 'Prioridad', sortable: true, sortKey: 'prioridad',
      filterable: false, resizable: true, width: 100,
      render: (row) => {
        const style = getPrioridadStyle(row.prioridad);
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.color}`}>{style.label}</span>;
      }
    },
    {
      key: 'alertas', label: 'Alertas', sortable: true, sortKey: 'alertas',
      filterable: false, resizable: true, width: 70,
      render: (row) => (
        <span className="flex items-center justify-center gap-1">
          <AlertTriangle size={14} className="text-orange-500" />
          {row.total_alertas || row.total_alertas_embebidas || (row.alertas?.length) || 0}
        </span>
      )
    },
    {
      key: 'vinculos', label: 'Vínculos', sortable: false,
      filterable: false, resizable: true, width: 100, defaultVisible: true,
      render: (row) => {
        if (!row.tiene_reincidencia && !row.tiene_vinculado) return <span className="text-slate-300 text-xs">-</span>;
        return (
          <span className="inline-flex flex-wrap items-center gap-1" data-testid={`case-vinculos-badge-${row.id}`}>
            {row.tiene_reincidencia && (
              <span title="Episodios reincidentes en los últimos 180 días"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-800 border border-orange-300"
                data-testid={`case-reincidente-badge-${row.id}`}>
                <GitMerge size={11} /> Reincidente {row.vinculos_count}
              </span>
            )}
            {row.tiene_vinculado && (
              <span title={row.tiene_case_links ? 'Caso vinculado' : 'Tiene casos anteriores'}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-300"
                data-testid={`case-vinculado-badge-${row.id}`}>
                Vinculado
              </span>
            )}
          </span>
        );
      }
    },
    {
      key: 'ros_proceso', label: 'ROS', sortable: false,
      filterable: false, resizable: true, width: 110, defaultVisible: true,
      render: (row) => {
        if (!row.tiene_ros_en_proceso) return <span className="text-slate-300 text-xs">-</span>;
        return (
          <span
            title="El cliente tiene un ROS en proceso (esperando compliance). No generar duplicados."
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-800 border border-violet-300"
            data-testid={`case-ros-proceso-badge-${row.id}`}
          >
            <FileText size={11} />
            ROS en proceso
          </span>
        );
      }
    },
    {
      key: 'waya_verif', label: 'Waya', sortable: false,
      filterable: false, resizable: true, width: 120, defaultVisible: true,
      render: (row) => {
        const wv = row.waya_verificacion;
        if (!wv || !wv.resultado) return <span className="text-slate-300 text-xs">-</span>;
        const map = {
          CON_CVU: { txt: '⚠️ Debía baja', cls: 'bg-red-100 text-red-800 border-red-300' },
          ACTIVA_INFO: { txt: 'Activa', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
          SIN_CVU: { txt: 'Sin CVU', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
          ERROR: { txt: 'Error', cls: 'bg-zinc-100 text-zinc-600 border-zinc-300' },
        };
        const m = map[wv.resultado] || map.ERROR;
        const txt = wv.es_bloqueada_waya ? 'Bloqueada' : m.txt;
        const cls = wv.es_bloqueada_waya ? 'bg-orange-100 text-orange-800 border-orange-300' : m.cls;
        return (
          <span
            title={`${wv.motivo || ''}${wv.fecha ? ' · ' + new Date(wv.fecha).toLocaleDateString('es-AR') : ''}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}
            data-testid={`case-waya-badge-${row.id}`}
          >
            <Shield size={11} />{txt}
          </span>
        );
      }
    },
    {
      key: 'solicitud_bloqueo', label: 'Solicitud', sortable: false,
      filterable: false, resizable: true, width: 130, defaultVisible: true,
      render: (row) => {
        if (row.tiene_solicitud_bloqueo) {
          const pend = row.solicitud_bloqueo_pendiente;
          return (
            <span
              title={pend
                ? 'Solicitud de bloqueo PENDIENTE (puede estar trabada, revisar)'
                : 'Solicitud de bloqueo cerrada'}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${pend ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-slate-100 text-slate-600 border-slate-300'}`}
              data-testid={`case-solicitud-bloqueo-badge-${row.id}`}
            >
              <Lock size={11} />
              {pend ? 'Bloqueo pend.' : 'Bloqueo'}
            </span>
          );
        }
        if (row.tiene_solicitudes) {
          return (
            <span
              title="El caso tiene solicitudes asociadas (no de bloqueo)"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200"
              data-testid={`case-solicitud-otra-badge-${row.id}`}
            >
              <FileText size={11} />
              Solicitud
            </span>
          );
        }
        return <span className="text-slate-300 text-xs">-</span>;
      }
    },
    {
      key: 'cuentas', label: 'Cuentas', sortable: true, sortKey: 'cuentas',
      filterable: false, resizable: true, width: 70,
      render: (row) => (
        <span className="flex items-center justify-center gap-1">
          <Users size={14} className="text-blue-500" />
          {row.total_cuentas || (row.cuentas_relacionadas?.length) || 0}
        </span>
      )
    },
    {
      key: 'movimientos', label: 'Movs.', sortable: true, sortKey: 'movimientos',
      filterable: false, resizable: true, width: 80, defaultVisible: true,
      render: (row) => (
        <span className="flex items-center justify-center gap-1" title="Movimientos del caso" data-testid={`case-movs-${row.id}`}>
          <Activity size={14} className="text-indigo-500" />
          {row.total_movimientos || 0}
        </span>
      )
    },
    {
      key: 'asignado', label: 'Asignado', sortable: false,
      filterable: false, resizable: true, width: 140, defaultVisible: true,
      render: (row) => row.responsable_nombre ? (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-semibold border border-emerald-200"><User size={12} />{row.responsable_nombre}</span>
      ) : row.grupo_asignado_nombre ? (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-violet-100 text-violet-800 rounded-full text-xs font-semibold border border-violet-200"><Users size={12} />{row.grupo_asignado_nombre}</span>
      ) : row.asignado_a ? (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold border border-amber-200"><User size={12} />{row.asignado_a.split('@')[0]}</span>
      ) : <span className="text-slate-400 text-xs italic">Sin asignar</span>
    },
    {
      key: 'email_status', label: 'Email', sortable: false,
      filterable: false, resizable: false, width: 50, defaultVisible: true,
      render: (row) => (
        <>
          {row.email_estado_solicitud === 'PENDIENTE' && <Clock size={16} className="text-amber-500 animate-pulse" title="Email pendiente" />}
          {row.email_estado_solicitud === 'ENVIADO' && <Mail size={16} className="text-green-600" title="Email enviado" />}
          {row.email_estado_solicitud === 'ERROR' && <Mail size={16} className="text-red-500" title="Error al enviar" />}
        </>
      )
    },
    {
      key: 'acciones', label: 'Acciones', sortable: false, alwaysVisible: true,
      filterable: false, resizable: false, width: 110,
      render: (row) => (
        <div className="flex justify-center gap-1">
          <Button variant="ghost" size="sm" onClick={(e) => {
            e.stopPropagation();
            const caseIds = cases.map(c => c.id);
            localStorage.setItem('caseNavIds', JSON.stringify(caseIds));
            const navFilters = { ...filters, search: searchDebounce, sort_by: sortConfig.key, sort_order: sortConfig.direction };
            localStorage.setItem('caseNavFilters', JSON.stringify(navFilters));
            const filterParam = encodeURIComponent(JSON.stringify(navFilters));
            navigate(`/m/gestion-de-casos/caso/${row.id}?nav_filters=${filterParam}`);
          }} data-testid={`view-case-${row.id}`} title="Ver caso"><Eye size={16} /></Button>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/links?cuenta=${row.cuentas_relacionadas?.[0] || ''}`); }}><Link2 size={16} /></Button>
        </div>
      )
    }
  ];

  const loadAvailableAlerts = async () => {
    try {
      const res = await api.get('/alerts', { params: { estado: 'NUEVA', limit: 50 } });
      setAvailableAlerts(res.data.filter(a => !a.caso_id));
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const loadGroups = async () => {
    try {
      const res = await api.get('/groups');
      setGroups(res.data);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const loadUsuarios = async () => {
    try {
      const res = await api.get('/users');
      setUsuarios(res.data.filter(u => u.activo !== false));
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  useEffect(() => {
    loadGroups();
    loadUsuarios();
  }, []);

  // Funciones de selección múltiple
  const toggleCaseSelection = (caseId) => {
    setSelectedCaseIds(prev => 
      prev.includes(caseId) 
        ? prev.filter(id => id !== caseId)
        : [...prev, caseId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedCaseIds.length === cases.length) {
      setSelectedCaseIds([]);
    } else {
      setSelectedCaseIds(cases.map(c => c.id));
    }
  };

  const handleCierreSinRos = async () => {
    setCerrandoSinRos(true);
    setCierreProgress({ done: 0, total: selectedCaseIds.length });
    try {
      const res = await api.post('/waya/cierre-definitivo-cvus', {
        case_ids: selectedCaseIds,
        bloquear_si_necesario: cierreBloquear,
        cerrar_casos: cierreCerrarCasos,
      });
      const jobId = res.data?.job_id;
      const total = res.data?.total || selectedCaseIds.length;
      let job = null;
      for (let i = 0; i < 300; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await api.get(`/waya/cierre-definitivo-cvus-job/${jobId}`);
        job = jr.data;
        const done = Object.values(job?.progreso || {}).filter((p) => ['done', 'omitido', 'error', 'completado'].includes(p.estado) || p.paso_actual === 'CERRAR').length;
        setCierreProgress({ done, total });
        if (job?.estado === 'completado') break;
      }
      const r = job?.resultado || {};
      if ((r.eliminadas || 0) > 0) {
        toast.success(`${r.eliminadas} cuenta(s) dada(s) de baja${r.no_ok?.length ? ` · ${r.no_ok.length} omitida(s)` : ''}`);
      } else {
        toast.warning(r.message || 'No se dio de baja ninguna cuenta (¿no estaban bloqueadas?)');
      }
      setShowCierreSinRosDialog(false);
      loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en el cierre definitivo');
    } finally {
      setCerrandoSinRos(false);
      setCierreProgress(null);
    }
  };

  const handleGenerarDesdeLista = async () => {
    const valores = listaValores.split(/[\n,;]+/).map((v) => v.trim()).filter(Boolean);
    if (valores.length === 0) { toast.error('Pegá al menos un valor'); return; }
    setGenerandoLista(true);
    setListaProgress({ procesados: 0, total: valores.length, creados: 0 });
    try {
      const res = await api.post('/cases/generar-desde-lista', {
        tipo: listaTipo,
        valores,
        agrupacion: listaAgrupacion,
        estado: listaEstado,
        prioridad: listaPrioridad,
        titulo: listaTitulo || null,
      });
      const jobId = res.data?.job_id;
      setGenerarJobId(jobId);
      let job = null;
      for (let i = 0; i < 1200; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const jr = await api.get(`/cases/generar-job/${jobId}`);
        job = jr.data;
        setListaProgress({ procesados: job?.procesados || 0, total: job?.total || valores.length, creados: job?.creados || 0 });
        if (job?.estado === 'completado') break;
      }
      const r = job?.resultado || {};
      if ((r.casos_creados || 0) > 0) {
        let msg = `${r.casos_creados} caso(s) creado(s)`;
        if (r.cancelado) msg = `Cancelado · ${msg}`;
        if (r.no_encontrados) msg += ` · ${r.no_encontrados} no encontrada(s)`;
        toast.success(msg);
      } else {
        toast.warning(r.message || 'No se creó ningún caso (no se encontraron datos)');
      }
      setShowGenerarListaDialog(false);
      loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar casos');
    } finally {
      setGenerandoLista(false);
      setListaProgress(null);
      setGenerarJobId(null);
    }
  };

  const handleCancelarGeneracion = async () => {
    if (!generarJobId) return;
    try {
      await api.post(`/cases/generar-job/${generarJobId}/cancelar`, {}, { skipAuthRedirect: true });
      toast.info('Cancelando… se detendrá en la próxima entrada (los casos creados se conservan)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cancelar');
    }
  };

  const openInformeDialog = () => {
    if (selectedCaseIds.length === 0) {
      toast.error('Seleccione al menos un caso');
      return;
    }
    setInformeTitulo('');
    setInformeResumen('');
    setShowInformeDialog(true);
  };

  const handleGenerarInforme = async () => {
    const columnasSel = Object.keys(informeColumnas).filter((k) => informeColumnas[k]);

    setGenerandoInforme(true);
    try {
      const response = await api.post('/informes/multi-caso/pdf', {
        case_ids: selectedCaseIds,
        titulo: informeTitulo || null,
        resumen_ejecutivo: informeResumen || null,
        incluir_kpis: informeSecciones.incluir_kpis,
        incluir_graficos: informeSecciones.incluir_graficos,
        incluir_tabla_resumen: informeSecciones.incluir_tabla_resumen,
        incluir_detalle: informeSecciones.incluir_detalle,
        incluir_alertas: informeSecciones.incluir_alertas,
        incluir_montos: informeSecciones.incluir_montos,
        columnas_resumen: columnasSel.length ? columnasSel : null,
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Informe_${selectedCaseIds.length}_casos_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Informe generado con ${selectedCaseIds.length} caso(s)`);
      setShowInformeDialog(false);
    } catch (error) {
      toast.error('Error al generar el informe');
    } finally {
      setGenerandoInforme(false);
    }
  };

  const handleRevalidarBulk = async () => {
    if (selectedCaseIds.length === 0) { toast.error('Seleccione al menos un caso'); return; }
    setRevalidandoBulk(true);
    setRevalidarProgress({ procesados: 0, total: selectedCaseIds.length });
    try {
      const res = await api.post('/cases/revalidar-bulk', { case_ids: selectedCaseIds });
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error('No se pudo iniciar la revalidación');
      let job = null;
      for (let i = 0; i < 240; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const jr = await api.get(`/cases/revalidar-job/${jobId}`);
        job = jr.data;
        setRevalidarProgress({ procesados: job?.procesados || 0, total: job?.total || selectedCaseIds.length });
        if (job?.estado === 'completado') break;
      }
      const r = job?.resultado || {};
      if ((r.movimientos_nuevos_total || 0) > 0) {
        toast.success(`Revalidación lista: ${r.casos_actualizados || 0} caso(s) con +${r.movimientos_nuevos_total} movimiento(s) nuevo(s).`);
      } else {
        toast.info('Revalidación lista: no se encontraron movimientos nuevos.');
      }
      loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al revalidar');
    } finally {
      setRevalidandoBulk(false);
      setRevalidarProgress(null);
    }
  };

  const handleVerificarCierreBulk = async () => {
    if (selectedCaseIds.length === 0) { toast.error('Seleccione al menos un caso'); return; }
    setVerificandoBulk(true);
    setVerificarProgress({ procesados: 0, total: selectedCaseIds.length });
    try {
      const res = await api.post('/waya/verificar-cierre-cvus', { case_ids: selectedCaseIds });
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error('No se pudo iniciar la verificación');
      let job = null;
      for (let i = 0; i < 240; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const jr = await api.get(`/waya/verificar-cierre-job/${jobId}`);
        job = jr.data;
        setVerificarProgress({ procesados: job?.procesados || 0, total: job?.total || selectedCaseIds.length });
        if (job?.estado === 'completado') break;
      }
      const resumen = job?.resumen || {};
      setVerificarResults({ resultados: job?.resultados || [], resumen });
      setVerificarFiltro('todos');
      setShowVerificarResults(true);
      loadCases(pagination.page);
      if ((resumen.con_cvu || 0) > 0) {
        toast.warning(`⚠️ ${resumen.con_cvu} cuenta(s) cerrada(s) siguen con CVU activo en Waya.`);
      } else {
        toast.success('Verificación lista: ninguna cuenta cerrada tiene CVU activo en Waya.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al verificar en Waya');
    } finally {
      setVerificandoBulk(false);
      setVerificarProgress(null);
      setSelectedCaseIds([]);
    }
  };

  const handleVerificarTodosFiltrados = async () => {
    setVerificandoTodos(true);
    try {
      const params = { limit: 20000 };
      if (filters.estado) params.estado = filters.estado;
      if (filters.prioridad) params.prioridad = filters.prioridad;
      if (filters.asignacion) params.asignacion = filters.asignacion;
      if (filters.cuenta_bloqueada) params.cuenta_bloqueada = filters.cuenta_bloqueada;
      if (filters.es_externo) params.es_externo = filters.es_externo;
      if (filters.min_alertas) {
        if (filters.min_alertas.startsWith('exact')) {
          const n = parseInt(filters.min_alertas.replace('exact', ''));
          params.min_alertas = n; params.max_alertas = n;
        } else { params.min_alertas = filters.min_alertas; }
      }
      if (filters.tiene_vinculos) params.tiene_vinculos = filters.tiene_vinculos;
      if (filters.ros_en_proceso) params.ros_en_proceso = filters.ros_en_proceso;
      if (filters.waya_verificacion) params.waya_verificacion = filters.waya_verificacion;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      if (filters.cvus && filters.cvus.trim()) params.cvus = filters.cvus;
      if (filters.cuits && filters.cuits.trim()) params.cuits = filters.cuits;
      if (filters.solicitud_bloqueo) params.solicitud_bloqueo = filters.solicitud_bloqueo;
      if (searchDebounce) params.search = searchDebounce;
      const navRes = await api.get('/cases/navigation-ids', { params, timeout: 60000 });
      const ids = navRes.data?.ids || [];
      if (!ids.length) { toast.error('No hay casos que cumplan el filtro actual'); return; }
      const res = await api.post('/waya/verificar-cierre-cvus', { case_ids: ids });
      if (!res.data?.job_id) throw new Error('No se pudo iniciar');
      toast.success(`Verificación de ${ids.length} caso(s) lanzada en segundo plano. Seguí el avance en "Historial de Ejecuciones".`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo lanzar la verificación masiva');
    } finally {
      setVerificandoTodos(false);
    }
  };

  const handleLimpiarVerificacionBulk = async () => {
    if (selectedCaseIds.length === 0) { toast.error('Seleccione al menos un caso'); return; }
    setLimpiandoVerif(true);
    try {
      const res = await api.post('/waya/limpiar-verificacion', { case_ids: selectedCaseIds });
      toast.success(`Verificación quitada de ${res.data?.limpiados || 0} caso(s). Quedaron sin verificar.`);
      loadCases(pagination.page);
      setSelectedCaseIds([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al quitar la verificación');
    } finally {
      setLimpiandoVerif(false);
    }
  };

  const handleEnviarEmailBulk = () => {    if (selectedCaseIds.length === 0) { toast.error('Seleccione al menos un caso'); return; }
    setShowEmailBulkCompose(true);
  };

  const onEmailBulkComplete = (r, sentItems) => {
    setEmailBulkResults({ ...r });
    setEmailBulkSentItems(sentItems || []);
    setShowEmailBulkResults(true);
    setCuentaReproceso('');
    api.get('/notifications/rotacion').then(res => setRotacionReproceso(res.data)).catch(() => setRotacionReproceso(null));
    if ((r.enviados || 0) > 0) {
      toast.success(`${r.enviados} email(s) enviado(s).${r.sin_email ? ` ${r.sin_email} sin destinatario.` : ''}${r.errores ? ` ${r.errores} con error.` : ''}`);
    } else {
      toast.warning(`No se envió ningún email. ${r.sin_email || 0} sin destinatario, ${r.errores || 0} con error.`);
    }
    setSelectedCaseIds([]);
    loadCases(pagination.page);
  };

  const reprocesarErrores = async () => {
    const errorIds = (emailBulkResults?.detalle || []).filter(d => d.resultado === 'ERROR').map(d => d.case_id);
    if (errorIds.length === 0) { toast.info('No hay errores para reprocesar'); return; }
    // Reusar el contenido/email compuesto original si lo tenemos; si no, dejar que el backend lo arme
    const sentMap = Object.fromEntries((emailBulkSentItems || []).map(it => [it.case_id, it]));
    const items = errorIds.map(cid => sentMap[cid] || { case_id: cid });
    setReprocesando(true);
    try {
      const payload = { items };
      if (cuentaReproceso) payload.forzar_cuenta = cuentaReproceso;
      const res = await api.post('/cases/enviar-email-bulk', payload);
      const jobId = res.data?.job_id;
      let job = null;
      for (let i = 0; i < 480; i++) {
        await new Promise(r => setTimeout(r, 2500));
        const jr = await api.get(`/cases/enviar-email-job/${jobId}`);
        job = jr.data;
        if (job?.estado === 'completado') break;
      }
      const rr = job?.resultado || {};
      // Combinar: los reintentos exitosos se restan de errores
      const okIds = new Set((rr.detalle || []).filter(d => d.resultado === 'ENVIADO').map(d => d.case_id));
      const nuevoDetalle = (emailBulkResults.detalle || []).map(d => {
        const re = (rr.detalle || []).find(x => x.case_id === d.case_id);
        return re ? re : d;
      });
      const enviados = nuevoDetalle.filter(d => d.resultado === 'ENVIADO').length;
      const sin_email = nuevoDetalle.filter(d => d.resultado === 'SIN_EMAIL').length;
      const errores = nuevoDetalle.filter(d => d.resultado === 'ERROR').length;
      setEmailBulkResults({ enviados, sin_email, errores, detalle: nuevoDetalle });
      toast.success(`Reproceso: ${okIds.size} enviado(s), ${errores} aún con error`);
      loadCases(pagination.page);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reprocesar');
    } finally {
      setReprocesando(false);
    }
  };

  const openGenRosDialog = async () => {
    if (selectedCaseIds.length === 0) { toast.error('Seleccione al menos un caso'); return; }
    setGenRosTipo('lavado_activos');
    setShowGenRosDialog(true);
    try {
      const [tplRes, tiposRes] = await Promise.all([
        api.get('/ros-config/templates'),
        api.get('/ros/tipos'),
      ]);
      setGenRosTemplates(tplRes.data || []);
      setGenRosTipos(tiposRes.data || []);
    } catch { /* usar defaults */ }
  };

  const handleGenRosBulk = async () => {
    if (selectedCaseIds.length === 0) return;
    setGenRosSubmitting(true);
    setGenRosProgress({ procesados: 0, total: selectedCaseIds.length });
    try {
      // Inicia el job en segundo plano (responde de inmediato, evita timeout 524 de Cloudflare)
      const res = await api.post('/ros/generar-desde-caso-bulk', {
        caso_ids: selectedCaseIds,
        template_id: genRosTemplate && genRosTemplate !== 'none' ? genRosTemplate : '',
        tipo_ros: genRosTipo || 'lavado_activos',
      });
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error('No se pudo iniciar la generación');

      // Polling del progreso
      const total = res.data.total || selectedCaseIds.length;
      let job = null;
      for (let i = 0; i < 600; i++) { // hasta ~20 min
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const jr = await api.get(`/ros/generar-bulk-job/${jobId}`, { timeout: 20000 });
          job = jr.data;
          setGenRosProgress({ procesados: job.procesados || 0, total });
          if (job.estado === 'completado' || job.estado === 'error') break;
        } catch { /* reintentar en el próximo ciclo */ }
      }

      const creados = (job && job.creados) || [];
      const errores = (job && job.errores) || [];
      toast.success(`${creados.length} ROS generado(s)${errores.length ? ` · ${errores.length} con error` : ''}`);
      if (errores.length) errores.slice(0, 3).forEach((e) => toast.error(`${(e.caso_id || '').slice(0, 8)}: ${e.error}`));
      setShowGenRosDialog(false);
      setSelectedCaseIds([]);
      if (creados.length) {
        toast('Podés revisarlos en Reportes ROS', {
          action: { label: 'Ir a Reportes ROS', onClick: () => navigate('/m/gestion-de-casos/ros-reports') },
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error generando ROS');
    } finally {
      setGenRosSubmitting(false);
      setGenRosProgress(null);
    }
  };


  const handleBulkClose = async () => {
    if (selectedCaseIds.length === 0) { toast.error('Seleccione al menos un caso'); return; }
    if (!bulkCloseData.tipo_resolucion) { toast.error('Seleccione un tipo de resolución'); return; }
    if (!bulkCloseData.observaciones.trim()) { toast.error('Ingrese las observaciones'); return; }
    setClosingBulk(true);
    try {
      const res = await api.post('/cases/bulk-close', {
        case_ids: selectedCaseIds,
        tipo_resolucion: bulkCloseData.tipo_resolucion,
        observaciones: bulkCloseData.observaciones,
      });
      toast.success(res.data.message);
      setSelectedCaseIds([]);
      setShowBulkCloseDialog(false);
      setBulkCloseData({ tipo_resolucion: '', observaciones: '' });
      loadCases(pagination.page);
    } catch (error) { toast.error('Error al cerrar casos'); }
    finally { setClosingBulk(false); }
  };

  const handleClasificacionBulk = async (tipo) => {
    if (selectedCaseIds.length === 0) { toast.error('Seleccione al menos un caso'); return; }
    setClasificando(true);
    try {
      const res = await api.post('/cases/clasificacion-bulk', { case_ids: selectedCaseIds, tipo });
      toast.success(res.data?.message || 'Clasificación actualizada');
      setShowClasifDialog(false);
      setSelectedCaseIds([]);
      loadCases(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar clasificación');
    } finally { setClasificando(false); }
  };

  const handleBulkChangeEstado = async () => {
    if (selectedCaseIds.length === 0) { toast.error('Seleccione al menos un caso'); return; }
    if (!bulkEstadoData.estado) { toast.error('Seleccione un estado'); return; }
    setChangingEstado(true);
    try {
      // Job en segundo plano (evita timeout 524 de Cloudflare con muchos casos)
      const res = await api.post('/cases/bulk-change-estado', {
        case_ids: selectedCaseIds,
        estado: bulkEstadoData.estado,
        observaciones: bulkEstadoData.observaciones,
      });
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error('No se pudo iniciar el cambio de estado');
      const total = res.data.total || selectedCaseIds.length;
      let job = null;
      for (let i = 0; i < 900; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const jr = await api.get(`/cases/bulk-estado-job/${jobId}`, { timeout: 20000 });
          job = jr.data;
          setBulkEstadoProgress({ procesados: job.procesados || 0, total });
          if (job.estado === 'completado' || job.estado === 'error') break;
        } catch { /* reintentar */ }
      }
      toast.success((job && job.resultado && job.resultado.message) || 'Estados actualizados');
      setSelectedCaseIds([]);
      setShowBulkEstadoDialog(false);
      setBulkEstadoData({ estado: '', observaciones: '' });
      loadCases(pagination.page);
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al cambiar estado'); }
    finally { setChangingEstado(false); setBulkEstadoProgress(null); }
  };


  const handleBulkAssign = async () => {
    if (selectedCaseIds.length === 0) {
      toast.error('Seleccione al menos un caso');
      return;
    }
    if ((!bulkAssignData.responsable_id || bulkAssignData.responsable_id === 'none') && 
        (!bulkAssignData.grupo_id || bulkAssignData.grupo_id === 'none')) {
      toast.error('Seleccione un responsable o grupo');
      return;
    }

    try {
      const requestData = {
        case_ids: selectedCaseIds
      };
      
      // Responsable
      if (bulkAssignData.responsable_id && bulkAssignData.responsable_id !== 'none') {
        const usuario = usuarios.find(u => u.id === bulkAssignData.responsable_id);
        requestData.responsable_id = bulkAssignData.responsable_id;
        requestData.responsable_nombre = usuario?.nombre || '';
      }
      
      // Grupo
      if (bulkAssignData.grupo_id && bulkAssignData.grupo_id !== 'none') {
        const grupo = groups.find(g => g.id === bulkAssignData.grupo_id);
        requestData.grupo_asignado_id = bulkAssignData.grupo_id;
        requestData.grupo_asignado_nombre = grupo?.nombre || '';
      }

      // Un solo request que envía 1 email resumen
      await api.post('/cases/bulk-assign', requestData);

      toast.success(`${selectedCaseIds.length} caso(s) asignado(s) correctamente`);
      setSelectedCaseIds([]);
      setShowBulkAssignDialog(false);
      setBulkAssignData({ responsable_id: '', grupo_id: '' });
      loadCases(pagination.page);
    } catch (error) {
      toast.error('Error al asignar casos');
    }
  };

  // ===== VIGILAR =====
  const pollVigilarJob = useCallback((jobId) => {
    if (vigilarPollRef.current) clearInterval(vigilarPollRef.current);
    const tick = async () => {
      try {
        const r = await api.get(`/cases/vigilar/${jobId}`, { skipAuthRedirect: true });
        setVigilarJob(r.data);
        if (['COMPLETADO', 'CANCELADO', 'ERROR'].includes(r.data?.estado)) {
          clearInterval(vigilarPollRef.current);
          vigilarPollRef.current = null;
          loadCases(pagination.page);
          if (r.data.estado === 'COMPLETADO') {
            toast.success(`VIGILAR finalizado: ${r.data.completados} OK, ${r.data.errores} con error`);
          }
        }
      } catch { /* reintentar en el próximo tick */ }
    };
    tick();
    vigilarPollRef.current = setInterval(tick, 4000);
  }, [pagination.page]); // eslint-disable-line react-hooks/exhaustive-deps

  const openVigilarDialog = async () => {
    if (selectedCaseIds.length === 0) { toast.error('Seleccioná al menos un caso'); return; }
    setShowVigilarDialog(true);
    setVigilarLoadingCfg(true);
    try {
      const [empRes, rubRes, defRes] = await Promise.all([
        api.get('/empresas', { params: { limit: 100 } }),
        api.get('/waya/rubros'),
        api.get('/cuentas-juridicas/config/sucursal-defaults'),
      ]);
      const linked = (Array.isArray(empRes.data) ? empRes.data : []).filter(e => e.waya_empresa_id);
      setVigilarEmpresas(linked);
      setVigilarRubros(rubRes.data?.rubros || []);
      const def = defRes.data || {};
      if (def.default_empresa_id && linked.some(e => e.id === def.default_empresa_id)) setVigilarEmpresaId(def.default_empresa_id);
      if (def.default_rubro_id) setVigilarRubroId(def.default_rubro_id);
    } catch {
      toast.error('Error al cargar empresas/comisiones');
    } finally {
      setVigilarLoadingCfg(false);
    }
  };

  const handleVigilar = async () => {
    if (!vigilarEmpresaId) { toast.error('Elegí la empresa por defecto'); return; }
    if (!vigilarRubroId) { toast.error('Elegí la comisión por defecto'); return; }
    setVigilarStarting(true);
    try {
      // FASE 1: preparación (chequeo pasivo) → navega a la pantalla de Preparar Vigilancia
      const res = await api.post('/cases/vigilar/preparar', {
        case_ids: selectedCaseIds,
        empresa_id: vigilarEmpresaId,
        rubro_id: vigilarRubroId,
      }, { skipAuthRedirect: true });
      setShowVigilarDialog(false);
      setSelectedCaseIds([]);
      if (res.data?.job_id) {
        navigate(`/m/gestion-de-casos/preparar-vigilancia/${res.data.job_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar la preparación de vigilancia');
    } finally {
      setVigilarStarting(false);
    }
  };

  const cancelVigilar = async () => {
    if (!vigilarJob?.job_id) return;
    try {
      await api.post(`/cases/vigilar/${vigilarJob.job_id}/cancelar`, {}, { skipAuthRedirect: true });
      toast.info('Cancelando VIGILAR (terminará el caso en curso)...');
    } catch { /* noop */ }
  };

  const handleMarcarConfigurada = async () => {
    if (selectedCaseIds.length === 0) { toast.error('Seleccioná al menos un caso'); return; }
    if (!window.confirm(`Marcar ${selectedCaseIds.length} caso(s) como "Configurada (manual)" y pasarlos a En Vigilancia, sin tocar Waya?`)) return;
    try {
      const res = await api.post('/cases/vigilancia-manual/bulk', { case_ids: selectedCaseIds }, { skipAuthRedirect: true });
      toast.success(`${res.data?.marcados || 0} caso(s) marcados En Vigilancia (manual)`);
      setSelectedCaseIds([]);
      loadCases(pagination.page);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo marcar los casos');
    }
  };

  // Re-enganchar el polling si hay un job VIGILAR en curso al entrar/volver a la pantalla
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/cases/vigilar-actual', { skipAuthRedirect: true });
        if (r.data?.job?.job_id) {
          setVigilarJob(r.data.job);
          pollVigilarJob(r.data.job.job_id);
        }
      } catch { /* noop */ }
    })();
    return () => { if (vigilarPollRef.current) clearInterval(vigilarPollRef.current); };
  }, [pollVigilarJob]);


  const handleSendToApproval = async () => {
    if (!selectedCase || !approvalData.descripcion || !approvalData.grupo_aprobador_id) {
      toast.error('Complete los campos requeridos');
      return;
    }
    
    try {
      await api.post('/approvals', {
        tipo: 'CASO',
        entidad_id: selectedCase.id,
        entidad_nombre: selectedCase.titulo,
        accion: approvalData.accion,
        descripcion: approvalData.descripcion,
        grupo_aprobador_id: approvalData.grupo_aprobador_id,
        prioridad: approvalData.prioridad
      });
      
      // Update case status
      await api.patch(`/cases/${selectedCase.id}`, { estado: 'PENDIENTE_APROBACION' });
      
      toast.success('Caso enviado a aprobación');
      setShowApprovalDialog(false);
      setShowDetailDialog(false);
      setApprovalData({ accion: 'CERRAR', descripcion: '', grupo_aprobador_id: '', prioridad: 'MEDIA' });
      loadCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar a aprobación');
    }
  };

  const handleDeriveCase = async () => {
    if (!selectedCase || !deriveData.grupo_destino_id || !deriveData.motivo) {
      toast.error('Complete los campos requeridos');
      return;
    }
    
    try {
      // Create an approval request for derivation
      await api.post('/approvals', {
        tipo: 'CASO',
        entidad_id: selectedCase.id,
        entidad_nombre: selectedCase.titulo,
        accion: 'ESCALAR',
        descripcion: `Caso derivado: ${deriveData.motivo}`,
        grupo_aprobador_id: deriveData.grupo_destino_id,
        prioridad: deriveData.prioridad || selectedCase.prioridad
      });
      
      // Update case to show it's been escalated
      await api.patch(`/cases/${selectedCase.id}`, { estado: 'EN_INVESTIGACION' });
      
      toast.success('Caso derivado correctamente');
      setShowDeriveDialog(false);
      setShowDetailDialog(false);
      setDeriveData({ grupo_destino_id: '', motivo: '', prioridad: '' });
      loadCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al derivar caso');
    }
  };

  const handleCreateCase = async () => {
    if (!newCase.titulo.trim()) {
      toast.error('El título es requerido');
      return;
    }
    
    try {
      await api.post('/cases', newCase);
      toast.success('Caso creado exitosamente');
      setShowCreateDialog(false);
      setNewCase({ titulo: '', descripcion: '', prioridad: 'MEDIA', tipo: 'ALERTA', alertas_ids: [], tags: [] });
      loadCases();
    } catch (error) {
      console.error('Error creating case:', error);
      toast.error('Error al crear el caso');
    }
  };

  const handleUpdateStatus = async (caseId, newStatus) => {
    try {
      const res = await api.patch(`/cases/${caseId}`, { estado: newStatus });
      
      // Verificar si requirió aprobación
      if (res.data._approval_required) {
        toast.info(
          `${res.data._approval_message}. Se creó una solicitud de aprobación.`,
          { duration: 6000 }
        );
        // Actualizar con el estado real (PENDIENTE_APROBACION)
        loadCases();
        if (selectedCase?.id === caseId) {
          setSelectedCase(prev => ({ ...prev, estado: res.data.estado }));
        }
      } else {
        toast.success('Estado actualizado');
        loadCases();
        if (selectedCase?.id === caseId) {
          setSelectedCase(prev => ({ ...prev, estado: newStatus }));
        }
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  // Función para verificar si un cambio de estado requiere aprobación
  const checkTransitionApproval = async (caseId, newStatus) => {
    try {
      const res = await api.get(`/cases/${caseId}/check-transition/${newStatus}`);
      return res.data;
    } catch (error) {
      console.error('Error checking transition:', error);
      return { requiere_aprobacion: false };
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedCase) return;
    
    try {
      const res = await api.post(`/cases/${selectedCase.id}/comments`, { texto: newComment });
      setSelectedCase(res.data);
      setNewComment('');
      toast.success('Comentario agregado');
    } catch (error) {
      toast.error('Error al agregar comentario');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.texto.trim() || !selectedCase) return;
    
    try {
      const res = await api.post(`/cases/${selectedCase.id}/notes`, newNote);
      setSelectedCase(res.data);
      setNewNote({ texto: '', tipo: 'nota' });
      setShowNoteForm(false);
      toast.success('Nota agregada al timeline');
    } catch (error) {
      toast.error('Error al agregar nota');
    }
  };

  const handleAddDocument = async () => {
    if (!newDocument.nombre.trim() || !newDocument.url.trim() || !selectedCase) {
      toast.error('Nombre y URL son requeridos');
      return;
    }
    
    try {
      const res = await api.post(`/cases/${selectedCase.id}/documents`, newDocument);
      setSelectedCase(res.data);
      setNewDocument({ nombre: '', url: '', tipo: 'documento' });
      setShowDocForm(false);
      toast.success('Documento agregado');
    } catch (error) {
      toast.error('Error al agregar documento');
    }
  };

  const handleUpdateCaseType = async (caseId, newType) => {
    try {
      await api.patch(`/cases/${caseId}`, { tipo: newType });
      toast.success('Tipo de caso actualizado');
      if (selectedCase?.id === caseId) {
        setSelectedCase(prev => ({ ...prev, tipo: newType }));
      }
      loadCases();
    } catch (error) {
      toast.error('Error al actualizar tipo');
    }
  };

  const handleUpdatePriority = async (caseId, newPriority) => {
    try {
      await api.patch(`/cases/${caseId}`, { prioridad: newPriority });
      toast.success('Prioridad actualizada');
      if (selectedCase?.id === caseId) {
        setSelectedCase(prev => ({ ...prev, prioridad: newPriority }));
      }
      loadCases();
    } catch (error) {
      toast.error('Error al actualizar prioridad');
    }
  };

  const handleSendEmail = async (caseData) => {
    if (!caseData) return;
    // Open email client with case info
    const subject = encodeURIComponent(`Caso: ${caseData.titulo || caseData.id}`);
    const body = encodeURIComponent(`Información del caso:\n\nTítulo: ${caseData.titulo}\nEstado: ${caseData.estado}\nPrioridad: ${caseData.prioridad}\n\nDescripción: ${caseData.descripcion || 'Sin descripción'}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const selectedLinkIds = Object.entries(selectedForLink).filter(([, v]) => v).map(([k]) => k);

  const analizarVinculacion = async () => {
    if (selectedLinkIds.length < 2) { toast.warning('Seleccioná al menos 2 casos'); return; }
    navigate(`/m/gestion-de-casos/analisis-vinculacion?ids=${selectedLinkIds.join(',')}`);
  };

  const unificarCasos = async () => {
    if (!window.confirm(`¿Unificar ${selectedLinkIds.length} casos? Los originales se cerrarán y se creará uno nuevo.`)) return;
    try {
      const res = await api.post('/cases/unificar', { case_ids: selectedLinkIds });
      toast.success(res.data.message);
      setShowVinculacion(false);
      setSelectedForLink({});
      setVinculacionResult(null);
      loadCases();
      navigate(`/m/gestion-de-casos/caso/${res.data.caso_padre_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openDetail = async (caseItem) => {
    try {
      const res = await api.get(`/cases/${caseItem.id}`);
      setSelectedCase(res.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Error cargando detalle');
    }
  };

  const formatTimelineDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date.toLocaleDateString();
  };

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Briefcase className="text-blue-600" />
            Gestión de Casos
          </h1>
          <p className="text-slate-600 mt-1">
            Agrupa y gestiona investigaciones complejas
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Button
            variant={showDashboard ? "default" : "outline"}
            onClick={() => setShowDashboard(!showDashboard)}
            className={showDashboard ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""}
            data-testid="toggle-dashboard-btn"
          >
            <BarChart3 size={16} className="mr-2" />
            {showDashboard ? 'Ver Lista' : 'Dashboard'}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/m/gestion-de-casos/casos-kanban')}
            data-testid="kanban-view-btn"
          >
            <LayoutGrid size={16} className="mr-2" />
            Kanban
          </Button>
          {!showDashboard && (
            <Button
              variant={viewMode === 'agrupada' ? 'default' : 'outline'}
              onClick={() => setViewMode(viewMode === 'agrupada' ? 'tabla' : 'agrupada')}
              className={viewMode === 'agrupada' ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}
              data-testid="toggle-grouped-view-btn"
            >
              <Layers size={16} className="mr-2" />
              {viewMode === 'agrupada' ? 'Vista Tabla' : 'Vista Agrupada'}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => navigate('/m/gestion-de-casos/estados-casos')}
              data-testid="manage-statuses-btn"
            >
              <Tags size={16} className="mr-2" />
              Estados
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => navigate('/m/gestion-de-casos/casos-duplicados')}
              data-testid="duplicates-btn"
            >
              <Copy size={16} className="mr-2" />
              Duplicados
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => navigate('/m/gestion-de-casos/backlog')}
              data-testid="backlog-btn"
              className="border-violet-300 text-violet-700 hover:bg-violet-50"
            >
              <Inbox size={16} className="mr-2" />
              Limpiar backlog
            </Button>
          )}
          {puedeEliminar && (
            <Button variant="outline" onClick={() => setShowPapelera(true)} className="border-red-200 text-red-700 hover:bg-red-50" data-testid="papelera-casos-btn">
              <Trash2 size={16} className="mr-2" /> Papelera
            </Button>
          )}
          <Button
            onClick={() => {
              loadAvailableAlerts();
              setShowCreateDialog(true);
            }}
            style={{ backgroundColor: '#2563eb' }}
            data-testid="create-case-btn"
          >
            <Plus size={16} className="mr-2" />
            Nuevo Caso
          </Button>
          <Button
            variant="outline"
            onClick={() => { setListaValores(''); setShowGenerarListaDialog(true); }}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="generar-desde-lista-btn"
          >
            <ListPlus size={16} className="mr-2" />
            Generar desde lista
          </Button>
          <ExcelExportButton type="cases" label="Excel" />
        </div>
      </div>

      {/* Contenido condicional: Dashboard o Lista */}
      {showDashboard ? (
        <CasosDashboard />
      ) : (
        <>
          {/* Stats Cards - Configurable KPI Cards */}
          <div className="flex items-center gap-2 mb-2">
            {isAdmin && (
              <button
                onClick={() => setShowKpiConfigDialog(true)}
                className="ml-auto text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors"
                data-testid="configure-kpi-cards-btn"
              >
                <Settings size={13} />
                Configurar tarjetas
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
            {(() => {
              const COLOR_MAP = {
                slate: { bg: 'bg-white', bgActive: 'bg-slate-100 ring-2 ring-blue-500', text: 'text-slate-900', label: 'text-slate-500', active: 'text-blue-600', border: 'border-slate-200', bgHover: 'hover:bg-slate-50' },
                blue: { bg: 'bg-blue-50 border-blue-200', bgActive: 'bg-blue-100 border-blue-400 ring-2 ring-blue-500', text: 'text-blue-700', label: 'text-blue-600', active: 'text-blue-700', bgHover: 'hover:bg-blue-100' },
                yellow: { bg: 'bg-yellow-50 border-yellow-200', bgActive: 'bg-yellow-100 border-yellow-400 ring-2 ring-yellow-500', text: 'text-yellow-700', label: 'text-yellow-600', active: 'text-yellow-700', bgHover: 'hover:bg-yellow-100' },
                green: { bg: 'bg-green-50 border-green-200', bgActive: 'bg-green-100 border-green-400 ring-2 ring-green-500', text: 'text-green-700', label: 'text-green-600', active: 'text-green-700', bgHover: 'hover:bg-green-100' },
                red: { bg: 'bg-red-50 border-red-200', bgActive: 'bg-red-100 border-red-400 ring-2 ring-red-500', text: 'text-red-700', label: 'text-red-600', active: 'text-red-700', bgHover: 'hover:bg-red-100' },
                orange: { bg: 'bg-orange-50 border-orange-200', bgActive: 'bg-orange-100 border-orange-400 ring-2 ring-orange-500', text: 'text-orange-700', label: 'text-orange-600', active: 'text-orange-700', bgHover: 'hover:bg-orange-100' },
                rose: { bg: 'bg-rose-50 border-rose-200', bgActive: 'bg-rose-100 border-rose-400 ring-2 ring-rose-500', text: 'text-rose-700', label: 'text-rose-600', active: 'text-rose-700', bgHover: 'hover:bg-rose-100' },
                purple: { bg: 'bg-purple-50 border-purple-200', bgActive: 'bg-purple-100 border-purple-400 ring-2 ring-purple-500', text: 'text-purple-700', label: 'text-purple-600', active: 'text-purple-700', bgHover: 'hover:bg-purple-100' },
                indigo: { bg: 'bg-indigo-50 border-indigo-200', bgActive: 'bg-indigo-100 border-indigo-400 ring-2 ring-indigo-500', text: 'text-indigo-700', label: 'text-indigo-600', active: 'text-indigo-700', bgHover: 'hover:bg-indigo-100' },
                gray: { bg: 'bg-gray-50 border-gray-200', bgActive: 'bg-gray-100 border-gray-400 ring-2 ring-gray-500', text: 'text-gray-700', label: 'text-gray-600', active: 'text-gray-700', bgHover: 'hover:bg-gray-100' },
                cyan: { bg: 'bg-cyan-50 border-cyan-200', bgActive: 'bg-cyan-100 border-cyan-400 ring-2 ring-cyan-500', text: 'text-cyan-700', label: 'text-cyan-600', active: 'text-cyan-700', bgHover: 'hover:bg-cyan-100' },
                amber: { bg: 'bg-amber-50 border-amber-200', bgActive: 'bg-amber-100 border-amber-400 ring-2 ring-amber-500', text: 'text-amber-700', label: 'text-amber-600', active: 'text-amber-700', bgHover: 'hover:bg-amber-100' },
              };
              
              const activeCards = kpiCardsConfig.active_cards?.length
                ? kpiCardsConfig.available_cards?.filter(c => kpiCardsConfig.active_cards.includes(c.id)) || []
                : kpiCardsConfig.available_cards?.slice(0, 7) || [];
              
              return activeCards.map(card => {
                const colors = COLOR_MAP[card.color] || COLOR_MAP.slate;
                const isFilterActive = card.filter_type === 'estado'
                  ? filters.estado === card.filter_value
                  : card.filter_type === 'prioridad'
                    ? filters.prioridad === card.filter_value
                    : card.filter_type === 'cuenta_bloqueada'
                      ? filters.cuenta_bloqueada === card.filter_value
                      : !filters.estado && !filters.prioridad && !card.filter_type;
                
                const handleClick = () => {
                  if (!card.filter_type) {
                    setFilters({ estado: '', prioridad: '', asignacion: '', cuenta_bloqueada: '' });
                  } else if (card.filter_type === 'estado') {
                    setFilters(f => ({ ...f, estado: f.estado === card.filter_value ? '' : card.filter_value }));
                  } else if (card.filter_type === 'prioridad') {
                    setFilters(f => ({ ...f, prioridad: f.prioridad === card.filter_value ? '' : card.filter_value }));
                  } else if (card.filter_type === 'cuenta_bloqueada') {
                    setFilters(f => ({ ...f, cuenta_bloqueada: f.cuenta_bloqueada === card.filter_value ? '' : card.filter_value }));
                  }
                };
                
                return (
                  <Card
                    key={card.id}
                    className={`p-4 cursor-pointer transition-all hover:shadow-md ${isFilterActive ? colors.bgActive : `${colors.bg} ${colors.bgHover}`}`}
                    onClick={handleClick}
                    data-testid={`filter-${card.id}`}
                  >
                    <div className={`text-2xl font-bold ${colors.text}`}>{stats[card.stat_key] || 0}</div>
                    <div className={`text-xs ${colors.label}`}>{card.label}</div>
                    {isFilterActive && (
                      <div className={`text-xs ${colors.active} mt-1 font-medium`}>● Filtro activo</div>
                    )}
                  </Card>
                );
              });
            })()}
          </div>

      {/* SLA Panel */}
      <CaseSLAPanel isAdmin={isAdmin} />

      {/* KPIs Dashboard */}
      {kpis && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Tendencia Semanal */}
          <Card className="p-4 col-span-2">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Tendencia Semanal (Abiertos vs Cerrados)</h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={kpis.tendencia_semanal}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="semana" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="abiertos" fill="#3b82f6" name="Abiertos" radius={[3,3,0,0]} />
                <Bar dataKey="cerrados" fill="#22c55e" name="Cerrados" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* Métricas clave */}
          <Card className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Métricas Clave</h3>
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="text-xs text-blue-600 font-medium">Tiempo Promedio de Resolución</div>
                <div className="text-2xl font-bold text-blue-800">{kpis.avg_resolution_days}d</div>
                <div className="text-xs text-blue-500">{kpis.avg_resolution_hours}h (basado en {kpis.total_cerrados_muestra} casos)</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-medium mb-2">Distribución por Prioridad</div>
                {kpis.distribucion_prioridad?.map(p => (
                  <div key={p.prioridad} className="flex items-center justify-between text-xs py-1 border-b border-slate-50">
                    <Badge variant="outline" className="text-xs">{p.prioridad || 'SIN PRIORIDAD'}</Badge>
                    <span className="font-semibold text-slate-700">{p.cantidad}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs text-slate-500 font-medium mb-2">Top Tipos de Alerta</div>
                {kpis.distribucion_tipo_alerta?.slice(0, 4).map(t => (
                  <div key={t.tipo} className="flex items-center justify-between text-xs py-1 border-b border-slate-50">
                    <span className="text-slate-600 truncate max-w-[150px]">{t.tipo}</span>
                    <span className="font-semibold text-slate-700">{t.cantidad}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <CasesFilters
        filters={filters}
        setFilters={setFilters}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        usuarios={usuarios}
        estados={ALL_ESTADOS}
        prioridades={PRIORIDADES}
        selectedLinkIds={selectedLinkIds}
        onAnalizarVinculacion={analizarVinculacion}
        vinculacionLoading={vinculacionLoading}
      />

      {/* Cobertura del filtro por CUITs (1 caso por CUIT) */}
      {cuitCoverage && (
        <div className="mb-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm" data-testid="cuit-coverage-panel">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium text-slate-700">Filtro por CUITs ({cuitCoverage.total_cuits}):</span>
            <span className="rounded-md bg-emerald-100 text-emerald-800 px-2 py-1 font-medium" data-testid="cuit-coverage-encontrados">
              Encontrados: {cuitCoverage.encontrados.length}
            </span>
            <span className={`rounded-md px-2 py-1 font-medium ${cuitCoverage.no_encontrados.length ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`} data-testid="cuit-coverage-noencontrados">
              No encontrados: {cuitCoverage.no_encontrados.length}
            </span>
            <span className="text-xs text-slate-400">(se muestra 1 caso por CUIT: abierto &gt; bloqueo &gt; más reciente)</span>
            <button onClick={() => setFilters(f => ({ ...f, cuits: '' }))} data-testid="cuit-coverage-clear"
              className="ml-auto text-xs text-slate-500 hover:text-slate-700 underline">Quitar filtro</button>
          </div>
          {cuitCoverage.no_encontrados.length > 0 && (
            <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-amber-800">CUITs no encontrados:</span>
                <button data-testid="cuit-coverage-copy"
                  onClick={() => { navigator.clipboard.writeText(cuitCoverage.no_encontrados.join('\n')); toast.success('CUITs no encontrados copiados'); }}
                  className="text-xs text-amber-700 hover:text-amber-900 underline">Copiar</button>
              </div>
              <div className="mt-1 max-h-24 overflow-y-auto font-mono text-xs text-amber-900 whitespace-pre-wrap">
                {cuitCoverage.no_encontrados.join(', ')}
              </div>
            </div>
          )}
        </div>
      )}

      {!showDashboard && (
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="cases-global-actions-bar">
          <Button size="sm" onClick={handleVerificarTodosFiltrados} disabled={verificandoTodos} variant="outline" className="border-emerald-400 text-emerald-800 hover:bg-emerald-50 bg-white" data-testid="bulk-verificar-todos-btn">
            <Shield size={14} className="mr-1.5" />
            {verificandoTodos ? 'Lanzando…' : 'Verificar TODOS en Waya (filtro)'}
          </Button>
          <span className="text-xs text-slate-500">Verifica en 2º plano todos los casos del filtro actual. Seguí el avance en Historial de Ejecuciones.</span>
        </div>
      )}

      {!showDashboard && selectedCaseIds.length > 0 && (
        <div className="sticky top-2 z-20 mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-blue-200 bg-blue-50/90 backdrop-blur px-3 py-2 shadow-sm" data-testid="bulk-actions-bar">
          <span className="text-sm font-semibold text-blue-800 mr-1">{selectedCaseIds.length} seleccionado(s):</span>
          <Button size="sm" onClick={() => setShowBulkCloseDialog(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-close-btn">
            <XCircle size={14} className="mr-1.5" /> Cerrar
          </Button>
          <Button size="sm" onClick={() => setShowBulkEstadoDialog(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="bulk-estado-btn">
            <RefreshCw size={14} className="mr-1.5" /> Cambiar estado
          </Button>
          <Button size="sm" onClick={() => setShowBulkAssignDialog(true)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="bulk-assign-btn">
            <Users size={14} className="mr-1.5" /> Asignar
          </Button>
          <Button size="sm" onClick={openInformeDialog} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="generate-report-btn">
            <FileText size={14} className="mr-1.5" /> Informe
          </Button>
          <Button size="sm" onClick={handleEnviarEmailBulk} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="bulk-enviar-email-btn">
            <Send size={14} className="mr-1.5" /> Enviar Email
          </Button>
          <Button size="sm" onClick={openGenRosDialog} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="bulk-generar-ros-btn">
            <Shield size={14} className="mr-1.5" /> Generar ROS
          </Button>
          <Button size="sm" onClick={handleRevalidarBulk} disabled={revalidandoBulk} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="bulk-revalidar-btn">
            <Zap size={14} className="mr-1.5" />
            {revalidandoBulk ? (revalidarProgress ? `Revalidando ${revalidarProgress.procesados}/${revalidarProgress.total}…` : 'Revalidando…') : 'Revalidar'}
          </Button>
          <Button size="sm" onClick={() => { setCierreBloquear(false); setCierreCerrarCasos(true); setShowCierreSinRosDialog(true); }} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="bulk-cierre-sin-ros-btn">
            <Trash2 size={14} className="mr-1.5" /> Cierre definitivo
          </Button>
          <Button size="sm" onClick={handleVerificarCierreBulk} disabled={verificandoBulk} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 bg-white" data-testid="bulk-verificar-cierre-btn">
            <Shield size={14} className="mr-1.5" />
            {verificandoBulk ? (verificarProgress ? `Verificando ${verificarProgress.procesados}/${verificarProgress.total}…` : 'Verificando…') : 'Verificar en Waya'}
          </Button>
          <Button size="sm" onClick={handleLimpiarVerificacionBulk} disabled={limpiandoVerif} variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-50 bg-white" data-testid="bulk-limpiar-verificacion-btn">
            <XCircle size={14} className="mr-1.5" />
            {limpiandoVerif ? 'Quitando…' : 'Quitar verificación'}
          </Button>
          <Button size="sm" onClick={openVigilarDialog} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="bulk-vigilar-btn">
            <Eye size={14} className="mr-1.5" /> Vigilar
          </Button>
          <Button size="sm" onClick={handleMarcarConfigurada} variant="outline" className="border-cyan-300 text-cyan-700 hover:bg-cyan-50 bg-white" data-testid="bulk-marcar-configurada-btn">
            <CheckCircle size={14} className="mr-1.5" /> Configurada
          </Button>
          <Button size="sm" onClick={() => setShowClasifDialog(true)} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 bg-white" data-testid="bulk-clasificar-btn">
            <Building2 size={14} className="mr-1.5" /> Clasificar
          </Button>
          {puedeEliminar && (
            <Button size="sm" onClick={() => setShowEliminarDialog(true)} className="bg-red-700 hover:bg-red-800 text-white" data-testid="bulk-eliminar-btn">
              <Trash2 size={14} className="mr-1.5" /> Eliminar
            </Button>
          )}
        </div>
      )}

      <EliminarCasosDialog
        open={showEliminarDialog}
        onOpenChange={setShowEliminarDialog}
        caseIds={selectedCaseIds}
        onDone={() => { setSelectedCaseIds([]); loadCases(); }}
      />
      <PapeleraCasosDialog open={showPapelera} onOpenChange={setShowPapelera} onCambio={() => loadCases()} />

      <EmailBulkDialog
        open={showEmailBulkCompose}
        onOpenChange={setShowEmailBulkCompose}
        caseIds={selectedCaseIds}
        api={api}
        onComplete={onEmailBulkComplete}
      />

      <Dialog open={showEmailBulkResults} onOpenChange={setShowEmailBulkResults}>
        <DialogContent className="max-w-2xl" data-testid="email-bulk-results-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send size={18} className="text-teal-600" /> Envío de emails</DialogTitle>
          </DialogHeader>
          {emailBulkResults && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="rounded-md bg-emerald-100 text-emerald-800 px-2 py-1 font-medium" data-testid="email-bulk-enviados">Enviados: {emailBulkResults.enviados || 0}</span>
                <span className="rounded-md bg-amber-100 text-amber-800 px-2 py-1 font-medium" data-testid="email-bulk-sinemail">Sin destinatario: {emailBulkResults.sin_email || 0}</span>
                <span className="rounded-md bg-red-100 text-red-800 px-2 py-1 font-medium" data-testid="email-bulk-errores">Errores: {emailBulkResults.errores || 0}</span>
              </div>
              <div className="max-h-[50vh] overflow-auto rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Caso</th>
                      <th className="text-left px-3 py-2">Resultado</th>
                      <th className="text-left px-3 py-2">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(emailBulkResults.detalle || []).map((r, idx) => (
                      <tr key={idx} className="border-t border-slate-100" data-testid={`email-bulk-row-${idx}`}>
                        <td className="px-3 py-2 font-medium text-slate-800">{r.titulo || r.case_id?.slice(0, 8)}</td>
                        <td className="px-3 py-2">
                          {r.resultado === 'ENVIADO' && <span className="rounded bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-semibold">ENVIADO</span>}
                          {r.resultado === 'SIN_EMAIL' && <span className="rounded bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-semibold">SIN EMAIL</span>}
                          {r.resultado === 'ERROR' && <span className="rounded bg-red-100 text-red-800 px-2 py-0.5 text-xs font-semibold">ERROR</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-600 text-xs">{r.email || r.motivo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(emailBulkResults.errores || 0) > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 pt-1">
                  {rotacionReproceso && rotacionReproceso.activas > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Reenviar desde:</span>
                      <select value={cuentaReproceso} onChange={e => setCuentaReproceso(e.target.value)}
                        data-testid="email-bulk-reproceso-cuenta-select"
                        className="h-9 text-xs rounded border border-slate-300 px-2 bg-white font-mono">
                        <option value="">Automático ({rotacionReproceso.cuenta_actual || '—'})</option>
                        {(rotacionReproceso.cuentas || []).filter(c => c.activo).map(c => (
                          <option key={c.email} value={c.email}>{c.email} ({c.enviados_hoy}/{c.limite})</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <Button onClick={reprocesarErrores} disabled={reprocesando}
                    data-testid="email-bulk-reprocesar-btn"
                    className="bg-red-600 hover:bg-red-700 text-white">
                    <RefreshCw size={14} className={`mr-1.5 ${reprocesando ? 'animate-spin' : ''}`} />
                    {reprocesando ? 'Reprocesando…' : `Reprocesar errores (${emailBulkResults.errores})`}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showVerificarResults} onOpenChange={setShowVerificarResults}>
        <DialogContent className="max-w-3xl" data-testid="verificar-cierre-results-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Shield size={18} /> Verificación de cuentas en Waya</DialogTitle>
          </DialogHeader>
          {verificarResults && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="rounded-md bg-emerald-100 text-emerald-800 px-2 py-1 font-medium" data-testid="verificar-sumario-sincvu">Sin CVU (OK): {verificarResults.resumen?.sin_cvu || 0}</span>
                <span className="rounded-md bg-red-100 text-red-800 px-2 py-1 font-medium" data-testid="verificar-sumario-concvu">⚠️ Debía baja y sigue activa: {verificarResults.resumen?.con_cvu || 0}</span>
                <span className="rounded-md bg-slate-100 text-slate-700 px-2 py-1 font-medium" data-testid="verificar-sumario-activainfo">Activa (cierre sin baja): {verificarResults.resumen?.activa_info || 0}</span>
                <span className="rounded-md bg-orange-100 text-orange-800 px-2 py-1 font-medium" data-testid="verificar-sumario-bloqueadas">Bloqueadas en Waya: {verificarResults.resumen?.bloqueadas_waya || 0}</span>
                <span className="rounded-md bg-zinc-100 text-zinc-700 px-2 py-1 font-medium" data-testid="verificar-sumario-errores">Errores: {verificarResults.resumen?.errores || 0}</span>
              </div>
              {(verificarResults.resumen?.con_cvu || 0) > 0 && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  ⚠️ Hay cuentas que debían estar dadas de baja (bloqueadas o con cierre definitivo) pero SIGUEN activas en Waya. Revisalas (posible reactivación externa).
                </p>
              )}
              <div className="flex flex-wrap gap-1.5">
                {[
                  { k: 'todos', label: 'Todos' },
                  { k: 'con_cvu', label: '⚠️ Debía baja y activa' },
                  { k: 'bloqueadas', label: 'Bloqueadas en Waya' },
                  { k: 'sin_cvu', label: 'Sin CVU' },
                  { k: 'activa_info', label: 'Activa (sin baja)' },
                ].map((f) => (
                  <button key={f.k} onClick={() => setVerificarFiltro(f.k)}
                    className={`text-xs rounded-full px-3 py-1 border transition-colors ${verificarFiltro === f.k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                    data-testid={`verificar-filtro-${f.k}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="max-h-[50vh] overflow-auto rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Caso / CVU</th>
                      <th className="text-left px-3 py-2">Estado en Waya</th>
                      <th className="text-left px-3 py-2">Resultado</th>
                      <th className="text-left px-3 py-2">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(verificarResults.resultados || [])
                      .filter((r) => {
                        if (verificarFiltro === 'todos') return true;
                        if (verificarFiltro === 'bloqueadas') return r.es_bloqueada_waya;
                        return r.resultado === verificarFiltro.toUpperCase();
                      })
                      .map((r, idx) => (
                      <tr key={idx} className="border-t border-slate-100" data-testid={`verificar-row-${idx}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800">{r.case_titulo || r.cvu}</div>
                          <div className="text-xs text-slate-500">{r.cvu_waya || r.cvu}{r.case_estado ? ` · ${r.case_estado}` : ''}</div>
                        </td>
                        <td className="px-3 py-2">
                          {r.es_bloqueada_waya
                            ? <span className="rounded bg-orange-100 text-orange-800 px-2 py-0.5 text-xs font-semibold">Bloqueada</span>
                            : <span className="text-xs text-slate-500">{r.estado_waya || '-'}</span>}
                        </td>
                        <td className="px-3 py-2">
                          {r.resultado === 'CON_CVU' && <span className="rounded bg-red-100 text-red-800 px-2 py-0.5 text-xs font-semibold">⚠️ DEBÍA BAJA</span>}
                          {r.resultado === 'ACTIVA_INFO' && <span className="rounded bg-slate-200 text-slate-700 px-2 py-0.5 text-xs font-semibold">Activa (sin baja)</span>}
                          {r.resultado === 'SIN_CVU' && <span className="rounded bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-semibold">SIN CVU</span>}
                          {r.resultado === 'ERROR' && <span className="rounded bg-zinc-200 text-zinc-700 px-2 py-0.5 text-xs font-semibold">ERROR</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-600 text-xs">{r.motivo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showClasifDialog} onOpenChange={setShowClasifDialog}>
        <DialogContent className="max-w-md" data-testid="clasificar-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 size={18} /> Clasificar {selectedCaseIds.length} caso(s)</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Marcá los casos seleccionados como internos o externos (override manual sobre el cálculo del padrón). Para asignar una cuenta puntual, hacelo desde el detalle del caso.</p>
          <div className="grid grid-cols-1 gap-2 py-2">
            <Button disabled={clasificando} onClick={() => handleClasificacionBulk('interno')} className="bg-emerald-600 hover:bg-emerald-700 text-white justify-start" data-testid="clasif-interno-btn">
              <Building2 size={16} className="mr-2" /> Marcar como INTERNO
            </Button>
            <Button disabled={clasificando} onClick={() => handleClasificacionBulk('externo')} className="bg-orange-600 hover:bg-orange-700 text-white justify-start" data-testid="clasif-externo-btn">
              <Building2 size={16} className="mr-2" /> Marcar como EXTERNO
            </Button>
            <Button disabled={clasificando} onClick={() => handleClasificacionBulk('auto')} variant="outline" className="justify-start" data-testid="clasif-auto-btn">
              <RefreshCw size={16} className="mr-2" /> Volver a automático (según padrón)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cases Table / Vista Agrupada */}
      {viewMode === 'agrupada' ? (
        <CasesGroupedView
          searchTerm={searchDebounce}
          filters={filters}
          estados={ALL_ESTADOS}
          onOpenCase={(id) => {
            if (!id) return;
            const navFilters = { ...filters, search: searchDebounce, sort_by: sortConfig.key, sort_order: sortConfig.direction };
            localStorage.setItem('caseNavFilters', JSON.stringify(navFilters));
            const filterParam = encodeURIComponent(JSON.stringify(navFilters));
            navigate(`/m/gestion-de-casos/caso/${id}?nav_filters=${filterParam}`);
          }}
        />
      ) : (
      <Card className="bg-white overflow-hidden p-4">
        <DataTable
          columns={caseColumns}
          data={cases}
          loading={loading}
          emptyMessage="No se encontraron casos"
          sortConfig={sortConfig}
          onSort={handleSort}
          columnFilters={filters}
          onColumnFilterChange={(key, value) => {
            if (['tipo'].includes(key)) {
              setFilters(prev => ({ ...prev, [key]: value }));
            }
          }}
          checkboxSelection={true}
          selectedIds={selectedCaseIds}
          onToggleSelect={toggleCaseSelection}
          onToggleSelectAll={toggleSelectAll}
          onRowClick={(row) => openDetail(row)}
          rowClassName={(row) => {
            const slaClass = row.sla_estado === 'VENCIDO' ? 'bg-red-50/50 hover:bg-red-50' : row.sla_estado === 'PROXIMO_VENCER' ? 'bg-amber-50/50 hover:bg-amber-50' : 'hover:bg-slate-50';
            return `${slaClass} cursor-pointer`;
          }}
          colSpanCount={caseColumns.length + 1}
        />
        
        {/* Paginación */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 mt-2">
            <div className="text-sm text-slate-600">
              Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total.toLocaleString()} casos
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handlePageChange(1)} disabled={pagination.page === 1 || loading} data-testid="pagination-first">Primera</Button>
              <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1 || loading} data-testid="pagination-prev">Anterior</Button>
              <span className="px-3 py-1 text-sm font-medium text-slate-700">Página {pagination.page} de {pagination.pages}</span>
              <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.pages || loading} data-testid="pagination-next">Siguiente</Button>
              <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.pages)} disabled={pagination.page === pagination.pages || loading} data-testid="pagination-last">Última</Button>
              <div className="ml-4 flex items-center gap-2 border-l pl-4">
                <span className="text-sm text-slate-600">Mostrar:</span>
                <Select value={String(pagination.limit)} onValueChange={handleLimitChange}>
                  <SelectTrigger className="w-20 h-8" data-testid="pagination-limit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </Card>
      )}
      </>
      )}


      {/* All dialogs extracted to CasesDialogs */}
      <CasesDialogs
        ESTADOS={ALL_ESTADOS} PRIORIDADES={PRIORIDADES} TIPOS_CASO={TIPOS_CASO}
        showCreateDialog={showCreateDialog} setShowCreateDialog={setShowCreateDialog}
        newCase={newCase} setNewCase={setNewCase}
        availableAlerts={availableAlerts} handleCreateCase={handleCreateCase}
        showDetailDialog={showDetailDialog} setShowDetailDialog={setShowDetailDialog}
        selectedCase={selectedCase}
        handleUpdateStatus={handleUpdateStatus} handleUpdateCaseType={handleUpdateCaseType}
        handleUpdatePriority={handleUpdatePriority}
        showNoteForm={showNoteForm} setShowNoteForm={setShowNoteForm}
        newNote={newNote} setNewNote={setNewNote} handleAddNote={handleAddNote}
        newComment={newComment} setNewComment={setNewComment} handleAddComment={handleAddComment}
        handleSendEmail={handleSendEmail} navigate={navigate}
        navigateToCase={(id) => {
          const navFilters = { ...filters, search: searchDebounce, sort_by: sortConfig.key, sort_order: sortConfig.direction };
          localStorage.setItem('caseNavFilters', JSON.stringify(navFilters));
          const filterParam = encodeURIComponent(JSON.stringify(navFilters));
          navigate(`/m/gestion-de-casos/caso/${id}?nav_filters=${filterParam}`);
        }}
        showApprovalDialog={showApprovalDialog} setShowApprovalDialog={setShowApprovalDialog}
        approvalData={approvalData} setApprovalData={setApprovalData}
        groups={groups} handleSendToApproval={handleSendToApproval}
        showDeriveDialog={showDeriveDialog} setShowDeriveDialog={setShowDeriveDialog}
        deriveData={deriveData} setDeriveData={setDeriveData}
        handleDeriveCase={handleDeriveCase}
        showBulkAssignDialog={showBulkAssignDialog} setShowBulkAssignDialog={setShowBulkAssignDialog}
        selectedCaseIds={selectedCaseIds} bulkAssignData={bulkAssignData} setBulkAssignData={setBulkAssignData}
        usuarios={usuarios} handleBulkAssign={handleBulkAssign}
        showKpiConfigDialog={showKpiConfigDialog} setShowKpiConfigDialog={setShowKpiConfigDialog}
        kpiCardsConfig={kpiCardsConfig} setKpiCardsConfig={setKpiCardsConfig} saveKpiCardsConfig={saveKpiCardsConfig}
      />

      {/* Modal de Análisis de Vinculación */}
      <Dialog open={showVinculacion} onOpenChange={setShowVinculacion}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge size={20} className="text-violet-600" />
              Análisis de Vinculación — {vinculacionResult?.total_casos} casos
            </DialogTitle>
          </DialogHeader>
          {vinculacionResult && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
                  <p className="text-2xl font-bold text-violet-700">{vinculacionResult.total_clusters}</p>
                  <p className="text-[10px] text-violet-500 uppercase">Grupos relacionados</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-2xl font-bold text-blue-700">{vinculacionResult.total_conexiones}</p>
                  <p className="text-[10px] text-blue-500 uppercase">Conexiones</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-2xl font-bold text-slate-700">{vinculacionResult.aislados?.length || 0}</p>
                  <p className="text-[10px] text-slate-500 uppercase">Sin relación</p>
                </div>
              </div>

              {/* Clusters */}
              {vinculacionResult.clusters?.map((cluster, ci) => (
                <div key={ci} className="border border-violet-200 rounded-xl overflow-hidden">
                  <div className="bg-violet-50 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GitMerge size={16} className="text-violet-600" />
                      <span className="font-bold text-violet-800">{cluster.label}</span>
                      <Badge className="bg-violet-200 text-violet-700">{cluster.total_casos} casos</Badge>
                      <Badge className="bg-red-100 text-red-700">Score max: {cluster.score_max}</Badge>
                    </div>
                    <Button size="sm" onClick={() => {
                      const ids = cluster.casos.map(c => c.id);
                      setSelectedForLink({});
                      ids.forEach(id => setSelectedForLink(prev => ({ ...prev, [id]: true })));
                      setShowVinculacion(false);
                      toast.info(`${ids.length} casos del cluster seleccionados. Volvé a clickear "Analizar" y luego "Unificar".`);
                    }} className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-7">
                      Seleccionar para unificar
                    </Button>
                  </div>

                  {/* Connections */}
                  <div className="px-4 py-3 space-y-2">
                    <p className="text-xs font-semibold text-slate-600 uppercase">Conexiones</p>
                    {cluster.conexiones?.slice(0, 10).map((cx, ei) => (
                      <div key={ei} className="flex items-center gap-2 text-xs p-2 bg-white rounded border border-slate-100">
                        <span className="font-medium text-slate-800 w-28 truncate" title={cx.from_nombre}>{cx.from_nombre}</span>
                        <div className="flex-1 flex flex-col items-center gap-0.5">
                          {cx.razones.map((r, ri) => (
                            <span key={ri} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${r.tipo === 'Transferencia directa' ? 'bg-red-100 text-red-700 border border-red-200' : r.tipo === 'Contraparte compartida' ? 'bg-purple-100 text-purple-700 border border-purple-200' : r.peso >= 80 ? 'bg-green-100 text-green-700' : r.peso >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                              {r.tipo}{r.tipo !== 'Transferencia directa' ? `: ${(r.detalle || '').slice(0, 50)}` : ''}
                            </span>
                          ))}
                        </div>
                        <span className="font-medium text-slate-800 w-28 truncate text-right" title={cx.to_nombre}>{cx.to_nombre}</span>
                      </div>
                    ))}
                    {(cluster.conexiones?.length || 0) > 10 && (
                      <p className="text-[10px] text-slate-400 text-center">... y {cluster.conexiones.length - 10} conexiones más</p>
                    )}
                  </div>

                  {/* Top counterparties */}
                  {cluster.top_contrapartes?.length > 0 && (
                    <div className="px-4 py-3 border-t border-violet-100 bg-purple-50/30">
                      <p className="text-xs font-semibold text-purple-700 uppercase mb-2">Contrapartes clave de la red</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {cluster.top_contrapartes.slice(0, 6).map((cp, cpi) => (
                          <div key={cpi} className="flex items-center justify-between text-xs p-2 bg-white rounded border border-purple-100">
                            <span className="font-medium text-slate-800 truncate">{cp.nombre || cp.cvu?.slice(0, 18)}</span>
                            <Badge className="bg-purple-100 text-purple-700 text-[9px]">{cp.total_txs} txs</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Cases in cluster */}
                  <div className="px-4 py-3 border-t border-violet-100">
                    <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Casos del grupo</p>
                    <div className="space-y-1">
                      {cluster.casos.map((c, ki) => (
                        <div key={ki} className="flex items-center justify-between text-xs p-2 rounded bg-slate-50 hover:bg-slate-100 cursor-pointer"
                          onClick={() => { setShowVinculacion(false); navigate(`/m/gestion-de-casos/caso/${c.id}`); }}>
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="font-bold text-slate-800 truncate">{c.nombre || c.cuit?.slice(0, 20)}</span>
                            {c.tipos_alerta?.slice(0, 2).map((t, ti) => (
                              <Badge key={ti} className="bg-slate-200 text-slate-600 text-[9px]">{t}</Badge>
                            ))}
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <Badge className="bg-slate-200 text-slate-600 text-[9px]">{c.estado}</Badge>
                            <span className="font-bold" style={{ color: c.score >= 80 ? '#ef4444' : c.score >= 50 ? '#f59e0b' : '#3b82f6' }}>{c.score}</span>
                            <span className="text-slate-400">{c.alertas} alertas</span>
                            {c.bloqueada && <Lock size={10} className="text-red-500" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* Isolated */}
              {vinculacionResult.aislados?.length > 0 && (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3">
                    <span className="font-semibold text-slate-600">Sin relación ({vinculacionResult.aislados.length})</span>
                  </div>
                  <div className="px-4 py-2 space-y-1">
                    {vinculacionResult.aislados.map((c, ai) => (
                      <div key={ai} className="flex items-center justify-between text-xs p-2 text-slate-500">
                        <span>{c.nombre || c.cuit?.slice(0, 20)}</span>
                        <span>{c.estado} | Score: {c.score}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVinculacion(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Cierre Masivo */}
      <Dialog open={showBulkCloseDialog} onOpenChange={setShowBulkCloseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle size={20} className="text-red-600" />
              Cerrar {selectedCaseIds.length} caso(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Tipo de resolución</label>
              <select
                value={bulkCloseData.tipo_resolucion}
                onChange={e => setBulkCloseData(p => ({ ...p, tipo_resolucion: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="bulk-close-tipo"
              >
                <option value="">Seleccionar...</option>
                {tiposResolucion.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Observaciones</label>
              <textarea
                value={bulkCloseData.observaciones}
                onChange={e => setBulkCloseData(p => ({ ...p, observaciones: e.target.value }))}
                rows={3}
                placeholder="Observaciones que se aplicarán a todos los casos seleccionados..."
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                data-testid="bulk-close-obs"
              />
            </div>
            <div className="p-2 bg-slate-50 rounded-lg text-xs text-slate-500">
              Se cerrarán <strong>{selectedCaseIds.length}</strong> casos con la misma resolución y observaciones.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkCloseDialog(false)}>Cancelar</Button>
            <Button onClick={handleBulkClose} disabled={closingBulk || !bulkCloseData.tipo_resolucion}
              className="bg-red-600 hover:bg-red-700" data-testid="bulk-close-confirm">
              {closingBulk ? 'Cerrando...' : `Cerrar ${selectedCaseIds.length} caso(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Cambiar Estado Masivo */}
      <Dialog open={showBulkEstadoDialog} onOpenChange={setShowBulkEstadoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw size={20} className="text-amber-600" />
              Cambiar estado — {selectedCaseIds.length} caso(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Nuevo estado</label>
              <select
                value={bulkEstadoData.estado}
                onChange={e => setBulkEstadoData(p => ({ ...p, estado: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="bulk-estado-select"
              >
                <option value="">Seleccionar...</option>
                {ALL_ESTADOS
                  .filter(e => !['CERRADO', 'PENDIENTE_APROBACION'].includes(e.value))
                  .map(e => (
                    <option key={e.value} value={e.value}>{e.label}</option>
                  ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">Para cerrar casos usá el botón "Cerrar" (lleva tipo de resolución).</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Observaciones (opcional)</label>
              <textarea
                value={bulkEstadoData.observaciones}
                onChange={e => setBulkEstadoData(p => ({ ...p, observaciones: e.target.value }))}
                rows={3}
                placeholder="Nota que se agrega al historial de cada caso..."
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                data-testid="bulk-estado-obs"
              />
            </div>
            <div className="p-2 bg-amber-50 rounded-lg text-xs text-amber-700">
              Se cambiará el estado de <strong>{selectedCaseIds.length}</strong> caso(s). Las transiciones que requieran aprobación quedarán como "Pendiente de aprobación".
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkEstadoDialog(false)} disabled={changingEstado}>Cancelar</Button>
            <Button onClick={handleBulkChangeEstado} disabled={changingEstado || !bulkEstadoData.estado}
              className="bg-amber-600 hover:bg-amber-700" data-testid="bulk-estado-confirm">
              {changingEstado
                ? (bulkEstadoProgress ? `Cambiando ${bulkEstadoProgress.procesados}/${bulkEstadoProgress.total}...` : 'Cambiando...')
                : `Cambiar estado (${selectedCaseIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Generar ROS desde casos seleccionados */}
      {/* Dialog: Cierre definitivo sin ROS */}
      <Dialog open={showCierreSinRosDialog} onOpenChange={setShowCierreSinRosDialog}>
        <DialogContent className="max-w-md" data-testid="cierre-sin-ros-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 size={20} /> Cierre definitivo (sin ROS)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>Vas a dar de baja el/los <strong>CVU</strong> en Waya de <strong>{selectedCaseIds.length}</strong> caso(s) seleccionado(s). Esta acción es <strong className="text-rose-700">irreversible</strong>.</p>
            <p className="text-xs text-slate-500">Por seguridad, solo se da de baja si la cuenta está <strong>bloqueada</strong>.</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={cierreBloquear} onCheckedChange={(v) => setCierreBloquear(!!v)} data-testid="cierre-bloquear-check" />
              Bloquear automáticamente si no está bloqueada
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={cierreCerrarCasos} onCheckedChange={(v) => setCierreCerrarCasos(!!v)} data-testid="cierre-cerrar-casos-check" />
              Cerrar los casos al finalizar
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCierreSinRosDialog(false)} disabled={cerrandoSinRos}>Cancelar</Button>
            <Button onClick={handleCierreSinRos} disabled={cerrandoSinRos} className="bg-rose-600 hover:bg-rose-700" data-testid="cierre-sin-ros-confirm">
              {cerrandoSinRos ? (cierreProgress ? `Procesando ${cierreProgress.done}/${cierreProgress.total}…` : 'Procesando…') : 'Dar de baja'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Generar casos desde lista */}
      <Dialog open={showGenerarListaDialog} onOpenChange={setShowGenerarListaDialog}>
        <DialogContent className="max-w-lg" data-testid="generar-lista-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListPlus size={20} className="text-blue-600" />
              Generar casos desde lista
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-sm font-medium">Tipo de lista</Label>
              <Select value={listaTipo} onValueChange={setListaTipo}>
                <SelectTrigger className="mt-1" data-testid="lista-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cvu">CVUs</SelectItem>
                  <SelectItem value="email">Emails</SelectItem>
                  <SelectItem value="cuit">CUITs</SelectItem>
                </SelectContent>
              </Select>
              {listaTipo === 'email' && (
                <p className="text-xs text-amber-600 mt-1">Los emails se buscan en Waya (puede tardar y limitar la IP si son muchos).</p>
              )}
            </div>
            <div>
              <Label className="text-sm font-medium">Lista (uno por línea o separados por coma)</Label>
              <Textarea
                value={listaValores}
                onChange={(e) => setListaValores(e.target.value)}
                rows={6}
                className="mt-1 text-sm font-mono"
                placeholder={listaTipo === 'email' ? 'juan@mail.com\nana@mail.com' : (listaTipo === 'cuit' ? '30-71890076-6\n30718900839' : '0000357302000000026866\n...')}
                data-testid="lista-valores"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Agrupación</Label>
                <Select value={listaAgrupacion} onValueChange={setListaAgrupacion}>
                  <SelectTrigger className="mt-1" data-testid="lista-agrupacion"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">1 caso por entrada</SelectItem>
                    <SelectItem value="uno">1 caso con todas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Prioridad</Label>
                <Select value={listaPrioridad} onValueChange={setListaPrioridad}>
                  <SelectTrigger className="mt-1" data-testid="lista-prioridad"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORIDADES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Estado inicial</Label>
              <Select value={listaEstado} onValueChange={setListaEstado}>
                <SelectTrigger className="mt-1" data-testid="lista-estado"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALL_ESTADOS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {listaAgrupacion === 'uno' && (
              <div>
                <Label className="text-sm font-medium">Título del caso (opcional)</Label>
                <Input value={listaTitulo} onChange={(e) => setListaTitulo(e.target.value)} className="mt-1" placeholder="Caso consolidado…" data-testid="lista-titulo" />
              </div>
            )}
          </div>
          <DialogFooter>
            {generandoLista && (
              <Button variant="destructive" onClick={handleCancelarGeneracion} data-testid="lista-cancelar-generacion">
                Detener generación
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowGenerarListaDialog(false)} disabled={generandoLista}>Cerrar</Button>
            <Button onClick={handleGenerarDesdeLista} disabled={generandoLista} className="bg-blue-600 hover:bg-blue-700" data-testid="lista-generar-confirm">
              {generandoLista ? (listaProgress ? `Procesando ${listaProgress.procesados}/${listaProgress.total} · ${listaProgress.creados || 0} creado(s)…` : 'Generando…') : 'Generar casos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Configurar Informe PDF multi-caso */}
      <Dialog open={showInformeDialog} onOpenChange={setShowInformeDialog}>
        <DialogContent className="max-w-lg" data-testid="informe-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} className="text-teal-600" />
              Generar Informe — {selectedCaseIds.length} caso(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-sm font-medium">Título (opcional)</Label>
              <Input
                value={informeTitulo}
                onChange={(e) => setInformeTitulo(e.target.value)}
                placeholder={`Informe Consolidado - ${selectedCaseIds.length} caso(s)`}
                className="mt-1"
                data-testid="informe-titulo-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Resumen ejecutivo (opcional)</Label>
              <Textarea
                value={informeResumen}
                onChange={(e) => setInformeResumen(e.target.value)}
                placeholder="Texto introductorio que aparecerá al inicio del informe…"
                className="mt-1 text-sm"
                rows={3}
                data-testid="informe-resumen-input"
              />
            </div>

            <div>
              <Label className="text-sm font-medium block mb-2">Secciones a incluir</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'incluir_kpis', label: 'Indicadores (KPIs)' },
                  { key: 'incluir_graficos', label: 'Gráficos' },
                  { key: 'incluir_tabla_resumen', label: 'Tabla resumen' },
                  { key: 'incluir_detalle', label: 'Detalle por caso' },
                  { key: 'incluir_alertas', label: 'Alertas' },
                  { key: 'incluir_montos', label: 'Montos' },
                ].map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <Checkbox
                      checked={informeSecciones[s.key]}
                      onCheckedChange={(v) => setInformeSecciones((prev) => ({ ...prev, [s.key]: !!v }))}
                      data-testid={`informe-seccion-${s.key}`}
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>

            {informeSecciones.incluir_tabla_resumen && (
              <div>
                <Label className="text-sm font-medium block mb-2">Columnas de la tabla resumen</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'cliente', label: 'Cliente' },
                    { key: 'cvu', label: 'CVU' },
                    { key: 'estado', label: 'Estado' },
                    { key: 'alertas', label: 'Alertas' },
                    { key: 'monto_periodo', label: 'Monto período', dep: 'incluir_montos' },
                    { key: 'monto_historico', label: 'Monto histórico', dep: 'incluir_montos' },
                  ].map((c) => {
                    const disabled = c.dep && !informeSecciones[c.dep];
                    return (
                      <label key={c.key} className={`flex items-center gap-2 text-sm cursor-pointer ${disabled ? 'text-slate-300' : 'text-slate-700'}`}>
                        <Checkbox
                          checked={informeColumnas[c.key] && !disabled}
                          disabled={disabled}
                          onCheckedChange={(v) => setInformeColumnas((prev) => ({ ...prev, [c.key]: !!v }))}
                          data-testid={`informe-columna-${c.key}`}
                        />
                        {c.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInformeDialog(false)} disabled={generandoInforme}>Cancelar</Button>
            <Button variant="outline" disabled={generandoInforme}
              className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="informe-editar-btn"
              onClick={() => {
                setShowInformeDialog(false);
                navigate('/m/gestion-de-casos/informe-editor', { state: { caseIds: selectedCaseIds } });
              }}>
              <Edit2 size={14} className="mr-1.5" /> Editar antes de exportar
            </Button>
            <Button onClick={handleGenerarInforme} disabled={generandoInforme}
              className="bg-teal-600 hover:bg-teal-700" data-testid="informe-generar-confirm">
              {generandoInforme ? 'Generando…' : 'Descargar PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showGenRosDialog} onOpenChange={setShowGenRosDialog}>
        <DialogContent className="max-w-md" data-testid="gen-ros-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield size={20} className="text-purple-600" />
              Generar ROS — {selectedCaseIds.length} caso(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              Se generará un borrador de ROS por cada caso seleccionado, prellenado con sus datos.
              Podrás editarlos y enviarlos a la UIF desde Reportes ROS.
            </p>
            <div>
              <Label className="text-sm font-medium">Motivo / Tipo de ROS</Label>
              <Select value={genRosTipo} onValueChange={setGenRosTipo}>
                <SelectTrigger className="mt-1" data-testid="gen-ros-tipo">
                  <SelectValue placeholder="Seleccioná el motivo" />
                </SelectTrigger>
                <SelectContent>
                  {genRosTipos.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Template de textos</Label>
              <Select value={genRosTemplate} onValueChange={setGenRosTemplate}>
                <SelectTrigger className="mt-1" data-testid="gen-ros-template">
                  <SelectValue placeholder="Sin template (textos vacíos)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin template</SelectItem>
                  {genRosTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenRosDialog(false)} disabled={genRosSubmitting}>Cancelar</Button>
            <Button onClick={handleGenRosBulk} disabled={genRosSubmitting}
              className="bg-purple-600 hover:bg-purple-700" data-testid="gen-ros-confirm">
              {genRosSubmitting
                ? (genRosProgress ? `Generando ${genRosProgress.procesados}/${genRosProgress.total}...` : 'Generando...')
                : `Generar ROS (${selectedCaseIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Dialog VIGILAR: empresa + comisión por defecto (se aplica a TODOS los seleccionados) */}
      <Dialog open={showVigilarDialog} onOpenChange={setShowVigilarDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={20} className="text-cyan-600" />
              Preparar vigilancia — {selectedCaseIds.length} caso(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              <strong>Fase 1 (chequeo pasivo):</strong> se buscará el mail y estado de cada cuenta en Waya, sin crear nada.
              En la próxima pantalla vas a poder ver el semáforo (Lista / Bloqueada / Ya configurada / Sin email) y
              enviar a la automatización <strong>solo las cuentas aptas</strong>.
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Empresa (Waya)</label>
              <select
                value={vigilarEmpresaId}
                onChange={e => setVigilarEmpresaId(e.target.value)}
                disabled={vigilarLoadingCfg}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                data-testid="vigilar-empresa"
              >
                <option value="">— Elegí empresa —</option>
                {vigilarEmpresas.map(e => (
                  <option key={e.id} value={e.id}>{e.razon_social} (#{e.waya_empresa_id})</option>
                ))}
              </select>
              {!vigilarLoadingCfg && vigilarEmpresas.length === 0 && (
                <p className="text-[11px] text-amber-600 mt-1">No hay empresas vinculadas a Waya.</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Comisión (Rubro)</label>
              <select
                value={vigilarRubroId}
                onChange={e => setVigilarRubroId(e.target.value)}
                disabled={vigilarLoadingCfg}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                data-testid="vigilar-rubro"
              >
                <option value="">— Elegí comisión —</option>
                {vigilarRubros.map(r => (
                  <option key={r.id} value={r.id}>{r.nombre}{r.porcentaje != null ? ` — ${r.porcentaje}%` : ''}</option>
                ))}
              </select>
            </div>
            <div className="p-2 bg-cyan-50 rounded-lg text-xs text-cyan-700">
              Esta empresa y comisión se aplican a los <strong>{selectedCaseIds.length}</strong> casos seleccionados.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVigilarDialog(false)}>Cancelar</Button>
            <Button onClick={handleVigilar} disabled={vigilarStarting || vigilarLoadingCfg || !vigilarEmpresaId || !vigilarRubroId}
              className="bg-cyan-600 hover:bg-cyan-700" data-testid="vigilar-confirm">
              {vigilarStarting ? 'Iniciando...' : `Preparar vigilancia (${selectedCaseIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Panel de progreso flotante del job VIGILAR */}
      {vigilarJob && (
        <div className="fixed bottom-4 right-4 w-96 max-w-[92vw] bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="vigilar-progress-panel">
          <div className="flex items-center justify-between px-4 py-2.5 bg-cyan-600 text-white">
            <div className="flex items-center gap-2 text-sm font-semibold">
              {vigilarJob.estado === 'PROCESANDO' ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />}
              Vigilar — {vigilarJob.completados || 0} OK · {vigilarJob.errores || 0} err / {vigilarJob.total}
            </div>
            <button onClick={() => setVigilarJob(null)} className="text-white/80 hover:text-white" data-testid="vigilar-panel-close">
              <XCircle size={18} />
            </button>
          </div>
          <div className="px-4 py-3 space-y-2">
            <div className="text-xs text-slate-600" data-testid="vigilar-current-step">{vigilarJob.current_step}</div>
            <div className="w-full bg-slate-100 rounded-full h-1.5">
              <div className="bg-cyan-500 h-1.5 rounded-full transition-all"
                style={{ width: `${vigilarJob.total ? Math.round(((vigilarJob.completados + vigilarJob.errores) / vigilarJob.total) * 100) : 0}%` }} />
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1 mt-1">
              {(vigilarJob.items || []).map((it, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px]" data-testid={`vigilar-item-${i}`}>
                  {it.estado === 'OK' && <CheckCircle size={12} className="text-green-600 mt-0.5 flex-shrink-0" />}
                  {it.estado === 'ERROR' && <AlertTriangle size={12} className="text-red-500 mt-0.5 flex-shrink-0" />}
                  {it.estado === 'PROCESANDO' && <Loader2 size={12} className="text-cyan-600 animate-spin mt-0.5 flex-shrink-0" />}
                  {it.estado === 'PENDIENTE' && <span className="w-3 h-3 rounded-full border border-slate-300 mt-0.5 flex-shrink-0" />}
                  <div className="min-w-0">
                    <span className="font-medium text-slate-700">{it.titulo}</span>
                    {it.mensaje && <span className="text-slate-500"> — {it.mensaje}</span>}
                  </div>
                </div>
              ))}
            </div>
            {vigilarJob.estado === 'PROCESANDO' && (
              <Button variant="outline" size="sm" onClick={cancelVigilar} className="w-full mt-1 text-red-600 border-red-200 hover:bg-red-50" data-testid="vigilar-cancel-btn">
                Cancelar proceso
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CasesPage;
